import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import './App.css';
import { hasSupabaseConfig, supabase } from './supabaseClient';
import {
  getBrowserNotificationPermission,
  isBrowserNotificationSupported,
  requestBrowserNotificationPermission,
  showBrowserNotification,
} from './browserNotifications';

const lockImage = require('./lock.png');

const getFallbackProfile = (user) => ({
  id: user.id,
  email: user.email || '',
  username: (user.email || '').split('@')[0] || 'user',
});

const isAbortError = (error) => {
  if (!error) return false;
  if (error.name === 'AbortError') return true;
  if (typeof error.message === 'string' && error.message.toLowerCase().includes('aborted')) return true;
  return false;
};

const isRlsViolationError = (error) => {
  if (!error) return false;
  if (typeof error.message === 'string' && error.message.toLowerCase().includes('row-level security')) return true;
  if (typeof error.code === 'string' && error.code === '42501') return true;
  return false;
};

const isSameChatId = (leftId, rightId) => {
  if (leftId === null || leftId === undefined || rightId === null || rightId === undefined) return false;
  return String(leftId) === String(rightId);
};

const hasSameMessageSnapshot = (currentMessages, nextMessages) => {
  if (currentMessages.length !== nextMessages.length) return false;

  for (let index = 0; index < currentMessages.length; index += 1) {
    const current = currentMessages[index];
    const next = nextMessages[index];

    if (
      String(current?.id ?? '') !== String(next?.id ?? '') ||
      current?.message_text !== next?.message_text ||
      (current?.message_type || 'text') !== (next?.message_type || 'text') ||
      (current?.media_url || '') !== (next?.media_url || '') ||
      Number(current?.duration_seconds || 0) !== Number(next?.duration_seconds || 0) ||
      current?.sent_at !== next?.sent_at ||
      Boolean(current?.is_read) !== Boolean(next?.is_read) ||
      current?.status !== next?.status
    ) {
      return false;
    }
  }

  return true;
};

const CHAT_EMOJIS = ['😀', '😂', '😍', '🥰', '😎', '🥳', '🤩', '😊', '😉', '😢', '😭', '😡', '🙏', '❤️', '👍', '🔥'];

const CHAT_SCROLL_BOTTOM_THRESHOLD = 80;
const CHAT_MEDIA_BUCKET = 'chat-media';
const RATE_OPTIONS = [100, 200, 325, 500, 700, 1000, 1500];
const CHAT_RATING_OPTIONS = [1, 2, 3, 4, 5];
const USERNAME_REGEX = /^[a-z0-9_]+$/;
const SESSION_BOOTSTRAP_TIMEOUT_MS = 12000;
const AUTH_SESSION_LOOKUP_TIMEOUT_MS = 8000;
const PROFILE_BOOTSTRAP_TIMEOUT_MS = 6000;
const SESSION_STUCK_HINT_DELAY_MS = 9000;
const CHAT_PREFERENCE_OPTIONS = [
  { value: 'both', label: 'Both Males & Females' },
  { value: 'male', label: 'Males Only' },
  { value: 'female', label: 'Females Only' },
  { value: 'none', label: 'No One (Pause All Requests)' },
];

const validateUsername = (rawUsername) => {
  const value = rawUsername ?? '';

  if (!value.trim()) {
    return 'Username is required.';
  }

  if (value !== value.trim()) {
    return 'Username cannot start or end with spaces.';
  }

  if (/\s/.test(value)) {
    return 'Username cannot contain spaces.';
  }

  if (/[A-Z]/.test(value)) {
    return 'Username must use lowercase letters only (capital letters are not allowed).';
  }

  if (value.length < 2 || value.length > 20) {
    return 'Username must be between 2 and 20 characters.';
  }

  if (!USERNAME_REGEX.test(value)) {
    return 'Username can only contain lowercase letters, numbers, and underscores (_).';
  }

  return null;
};

const hasValidProfileUsername = (profile) => {
  const username = profile?.username;
  if (typeof username !== 'string') return false;
  return !validateUsername(username.trim());
};

const getGenderIndicator = (gender) => {
  const normalizedGender = String(gender || '').trim().toLowerCase();

  if (normalizedGender === 'female') {
    return { symbol: '\u2640', label: 'Female', className: 'chat-gender-indicator-female' };
  }

  if (normalizedGender === 'male') {
    return { symbol: '\u2642', label: 'Male', className: 'chat-gender-indicator-male' };
  }

  return null;
};

const getPendingProfile = (user) => ({
  id: user.id,
  email: user.email || '',
  username: '',
});

const buildUsernameSuggestion = (user) => {
  const localPart = String(user?.email || '')
    .split('@')[0]
    .toLowerCase()
    .replace(/[^a-z0-9_]/g, '_')
    .replace(/_+/g, '_')
    .replace(/^_+|_+$/g, '');

  const fallbackSuffix = String(user?.id || '')
    .toLowerCase()
    .replace(/[^a-z0-9]/g, '')
    .slice(0, 6);
  const fallback = fallbackSuffix ? `user_${fallbackSuffix}` : 'user';

  const suggestion = (localPart || fallback).slice(0, 20);
  if (suggestion.length >= 2) return suggestion;
  return `${suggestion}${fallback}`.slice(0, 20);
};

const formatDurationLabel = (durationSeconds) => {
  const safeDuration = Number.isFinite(Number(durationSeconds))
    ? Math.max(0, Math.round(Number(durationSeconds)))
    : 0;
  const minutes = Math.floor(safeDuration / 60);
  const seconds = safeDuration % 60;
  return `${minutes}:${String(seconds).padStart(2, '0')}`;
};

const getAudioExtension = (mimeType) => {
  if (!mimeType) return 'webm';
  if (mimeType.includes('ogg')) return 'ogg';
  if (mimeType.includes('mp4')) return 'm4a';
  if (mimeType.includes('mpeg')) return 'mp3';
  if (mimeType.includes('wav')) return 'wav';
  return 'webm';
};

const blobToDataUrl = (blob) =>
  new Promise((resolve, reject) => {
    const fileReader = new FileReader();
    fileReader.onloadend = () => {
      if (typeof fileReader.result === 'string') {
        resolve(fileReader.result);
        return;
      }
      reject(new Error('Failed to convert voice note for sending.'));
    };
    fileReader.onerror = () => {
      reject(new Error('Failed to read voice note data.'));
    };
    fileReader.readAsDataURL(blob);
  });

const normalizeStoragePath = (value) => {
  if (!value) return '';
  return String(value).trim().replace(/^[\\/]+/, '').replace(/[?#].*$/, '');
};

const getStorageObjectPathCandidates = (urlOrPath, bucketName) => {
  if (!urlOrPath || !bucketName) return [];

  const extracted = new Set();
  const addExtractedPath = (value) => {
    const normalized = normalizeStoragePath(value);
    if (normalized) {
      extracted.add(normalized);
    }
  };

  let parsedUrl = null;
  try {
    parsedUrl = new URL(urlOrPath);
  } catch (_error) {
    parsedUrl = null;
  }

  if (!parsedUrl) {
    addExtractedPath(urlOrPath);
  }

  if (parsedUrl) {
    const decodedPath = decodeURIComponent(parsedUrl.pathname || '');
    const markers = [
      `/storage/v1/object/public/${bucketName}/`,
      `/storage/v1/object/sign/${bucketName}/`,
      `/object/public/${bucketName}/`,
      `/object/sign/${bucketName}/`,
      `/${bucketName}/`,
    ];

    markers.forEach((marker) => {
      if (decodedPath.includes(marker)) {
        addExtractedPath(decodedPath.split(marker)[1] || '');
      }
    });
  }

  const candidates = new Set();
  extracted.forEach((item) => {
    const normalized = normalizeStoragePath(item);
    if (!normalized) return;
    candidates.add(normalized);
    if (normalized.startsWith(`${bucketName}/`)) {
      candidates.add(normalized.slice(bucketName.length + 1));
    } else {
      candidates.add(`${bucketName}/${normalized}`);
    }
  });

  return [...candidates].filter(Boolean);
};

const withTimeout = (promise, timeoutMs, timeoutMessage = 'Operation timed out') =>
  new Promise((resolve, reject) => {
    const timeoutId = window.setTimeout(() => {
      reject(new Error(timeoutMessage));
    }, timeoutMs);

    Promise.resolve(promise)
      .then((value) => {
        window.clearTimeout(timeoutId);
        resolve(value);
      })
      .catch((error) => {
        window.clearTimeout(timeoutId);
        reject(error);
      });
  });

function UnlockAppPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [isCheckingSession, setIsCheckingSession] = useState(true);
  const [showSessionStuckHint, setShowSessionStuckHint] = useState(false);
  const [profile, setProfile] = useState(null);
  const [pendingAuthUser, setPendingAuthUser] = useState(null);
  const [usernameDraft, setUsernameDraft] = useState('');
  const [usernameSetupError, setUsernameSetupError] = useState('');
  const [savingUsernameSetup, setSavingUsernameSetup] = useState(false);
  const [activeChats, setActiveChats] = useState([]);
  const [loadingChats, setLoadingChats] = useState(false);
  const [chatsError, setChatsError] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searchingUsers, setSearchingUsers] = useState(false);
  const [searchError, setSearchError] = useState('');
  const [hasSearchedUsers, setHasSearchedUsers] = useState(false);
  const [selectedChat, setSelectedChat] = useState(null);
  const [chatMessages, setChatMessages] = useState([]);
  const [chatOpen, setChatOpen] = useState(false);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [messagesError, setMessagesError] = useState('');
  const [newMessage, setNewMessage] = useState('');
  const [sendingMessage, setSendingMessage] = useState(false);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [voiceError, setVoiceError] = useState('');
  const [isRecordingVoice, setIsRecordingVoice] = useState(false);
  const [recordingSeconds, setRecordingSeconds] = useState(0);
  const [uploadingVoice, setUploadingVoice] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [settingsTab, setSettingsTab] = useState('general');
  const [notificationPermission, setNotificationPermission] = useState(() => getBrowserNotificationPermission());
  const [requestingNotificationPermission, setRequestingNotificationPermission] = useState(false);
  const [userRate, setUserRate] = useState(100);
  const [userChatPreference, setUserChatPreference] = useState('both');
  const [userInstapay, setUserInstapay] = useState('');
  const [avatarPreview, setAvatarPreview] = useState('');
  const [selectedAvatarFile, setSelectedAvatarFile] = useState(null);
  const [savingGeneral, setSavingGeneral] = useState(false);
  const [savingPayment, setSavingPayment] = useState(false);
  const [settingsAlert, setSettingsAlert] = useState({ type: '', message: '' });
  const [unlockModalOpen, setUnlockModalOpen] = useState(false);
  const [currentUnlockTarget, setCurrentUnlockTarget] = useState(null);
  const [targetInstapay, setTargetInstapay] = useState('');
  const [paymentProofFile, setPaymentProofFile] = useState(null);
  const [paymentProofPreview, setPaymentProofPreview] = useState('');
  const [unlockingUsername, setUnlockingUsername] = useState('');
  const [submittingPaymentProof, setSubmittingPaymentProof] = useState(false);
  const [unlockError, setUnlockError] = useState('');
  const [unratedChats, setUnratedChats] = useState([]);
  const [ratingModalOpen, setRatingModalOpen] = useState(false);
  const [currentRatingChat, setCurrentRatingChat] = useState(null);
  const [selectedRating, setSelectedRating] = useState(null);
  const [submittingRating, setSubmittingRating] = useState(false);
  const [ratingError, setRatingError] = useState('');
  const [copyProfileLinkState, setCopyProfileLinkState] = useState('');
  const chatBodyRef = useRef(null);
  const messagesEndRef = useRef(null);
  const shouldStickToBottomRef = useRef(true);
  const emojiPickerRef = useRef(null);
  const avatarInputRef = useRef(null);
  const paymentProofInputRef = useRef(null);
  const recordingMediaRecorderRef = useRef(null);
  const recordingStreamRef = useRef(null);
  const recordingChunksRef = useRef([]);
  const recordingStartedAtRef = useRef(0);
  const recordingIntervalRef = useRef(null);
  const skippedRatingChatIdsRef = useRef(new Set());
  const notifiedUnlockChatIdsRef = useRef(new Set());
  const knownActiveChatIdsRef = useRef(new Set());
  const activeChatNotificationUsernameRef = useRef('');
  const knownUnreadCountsRef = useRef(new Map());
  const unreadNotificationUsernameRef = useRef('');
  const notifiedMessageIdsRef = useRef(new Set());
  const attemptedAutoUnlockKeyRef = useRef('');
  const selectedChatId = selectedChat?.id || null;
  const supportsVoiceNotes =
    typeof window !== 'undefined' &&
    typeof navigator !== 'undefined' &&
    Boolean(window.MediaRecorder) &&
    Boolean(navigator.mediaDevices?.getUserMedia);

  const displayName = useMemo(() => {
    if (!profile) return 'User';
    return profile.username || profile.email || 'User';
  }, [profile]);

  const requestedUnlockUsername = useMemo(() => {
    const searchParams = new URLSearchParams(location.search);
    const unlockValue = searchParams.get('unlock');
    return unlockValue ? unlockValue.trim() : '';
  }, [location.search]);

  const profileShareUrl = useMemo(() => {
    if (typeof window === 'undefined' || !profile?.username) return '';
    return `${window.location.origin}/u/${encodeURIComponent(profile.username)}`;
  }, [profile?.username]);

  const getAvatarInitial = (name) => {
    if (!name) return 'U';
    return name.trim().charAt(0).toUpperCase() || 'U';
  };

  const handleCopyProfileLink = useCallback(async () => {
    if (!profileShareUrl) return;

    try {
      if (typeof navigator !== 'undefined' && navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(profileShareUrl);
      } else {
        const fallbackInput = document.createElement('textarea');
        fallbackInput.value = profileShareUrl;
        fallbackInput.setAttribute('readonly', '');
        fallbackInput.style.position = 'fixed';
        fallbackInput.style.opacity = '0';
        document.body.appendChild(fallbackInput);
        fallbackInput.focus();
        fallbackInput.select();
        document.execCommand('copy');
        document.body.removeChild(fallbackInput);
      }
      setCopyProfileLinkState('Link copied');
    } catch (_error) {
      setCopyProfileLinkState('Copy failed');
    }
  }, [profileShareUrl]);

  const getTimeRemaining = (chatExpiryTime) => {
    if (!chatExpiryTime) return 'Unknown';

    const expiry = new Date(chatExpiryTime);
    if (Number.isNaN(expiry.getTime())) return 'Unknown';

    const diffMs = expiry.getTime() - Date.now();
    if (diffMs <= 0) return 'Expired';

    const hours = Math.floor(diffMs / (1000 * 60 * 60));
    const minutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));

    if (hours > 0) return `${hours}h ${minutes}m left`;
    return `${minutes}m left`;
  };

  const getRatingSelectionLabel = (ratingValue) => {
    if (ratingValue === null || ratingValue === undefined) return 'No rating selected';
    if (ratingValue === 0) return 'Selected: 0 stars (No response)';
    return `Selected: ${ratingValue} star${ratingValue > 1 ? 's' : ''}`;
  };

  const earnings = useMemo(() => {
    const appFee = Math.round(userRate * 0.13);
    const userEarnings = Math.round(userRate - userRate * 0.13);
    return {
      rate: userRate,
      appFee,
      userEarnings,
    };
  }, [userRate]);

  const activeChatPartners = useMemo(() => {
    return new Set(activeChats.map((chat) => chat.otherUsername).filter(Boolean));
  }, [activeChats]);

  const sortChatsByPriority = useCallback((chats) => {
    return [...chats].sort((a, b) => {
      const unreadA = a?.unreadCount || 0;
      const unreadB = b?.unreadCount || 0;
      if (unreadB !== unreadA) return unreadB - unreadA;

      const timeA = new Date(a?.chat_start_time || a?.chat_expiry_time || 0).getTime();
      const timeB = new Date(b?.chat_start_time || b?.chat_expiry_time || 0).getTime();
      return timeB - timeA;
    });
  }, []);

  const formatMessageTime = (timestamp) => {
    if (!timestamp) return '';
    const date = new Date(timestamp);
    if (Number.isNaN(date.getTime())) return '';
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const getMessageDeliveryState = (message) => {
    if (!message) return 'sent';
    if (message.is_read || message.status === 'read') return 'seen';
    return 'sent';
  };

  const isScrolledNearBottom = (element) => {
    if (!element) return true;
    const distanceFromBottom = element.scrollHeight - element.scrollTop - element.clientHeight;
    return distanceFromBottom <= CHAT_SCROLL_BOTTOM_THRESHOLD;
  };

  const handleChatBodyScroll = useCallback(() => {
    shouldStickToBottomRef.current = isScrolledNearBottom(chatBodyRef.current);
  }, []);

  const markChatAsRead = useCallback(async (chatId, currentUsername) => {
    if (!chatId || !currentUsername) return;
    try {
      await supabase
        .from('messages')
        .update({ is_read: true, status: 'read' })
        .eq('chat_request_id', chatId)
        .eq('receiver_username', currentUsername)
        .eq('is_read', false);
    } catch (error) {
      if (!isAbortError(error)) {
        // eslint-disable-next-line no-console
        console.error('Failed to mark messages as read:', error);
      }
    }
  }, []);

  const clearVoiceRecordingInterval = useCallback(() => {
    if (recordingIntervalRef.current) {
      window.clearInterval(recordingIntervalRef.current);
      recordingIntervalRef.current = null;
    }
  }, []);

  const releaseVoiceRecordingStream = useCallback(() => {
    if (!recordingStreamRef.current) return;
    recordingStreamRef.current.getTracks().forEach((track) => track.stop());
    recordingStreamRef.current = null;
  }, []);

  const discardVoiceRecording = useCallback(() => {
    clearVoiceRecordingInterval();

    const recorder = recordingMediaRecorderRef.current;
    if (recorder && recorder.state !== 'inactive') {
      recorder.ondataavailable = null;
      recorder.onstop = null;
      recorder.onerror = null;
      try {
        recorder.stop();
      } catch (_stopError) {
        // no-op
      }
    }

    recordingMediaRecorderRef.current = null;
    recordingChunksRef.current = [];
    recordingStartedAtRef.current = 0;
    releaseVoiceRecordingStream();
    setIsRecordingVoice(false);
    setRecordingSeconds(0);
  }, [clearVoiceRecordingInterval, releaseVoiceRecordingStream]);

  const uploadVoiceNoteFile = useCallback(async ({ audioBlob, chatId, senderUsername }) => {
    const fileExtension = getAudioExtension(audioBlob?.type || '');
    const uniquePart = Math.random().toString(36).slice(2, 10);
    const fileName = `${senderUsername || 'user'}-${Date.now()}-${uniquePart}.${fileExtension}`;
    const filePath = `voice-notes/${chatId}/${fileName}`;

    const { error: uploadError } = await supabase.storage.from(CHAT_MEDIA_BUCKET).upload(filePath, audioBlob, {
      cacheControl: '3600',
      upsert: false,
      contentType: audioBlob?.type || undefined,
    });

    if (uploadError) {
      throw uploadError;
    }

    const { data: publicData } = supabase.storage.from(CHAT_MEDIA_BUCKET).getPublicUrl(filePath);
    if (!publicData?.publicUrl) {
      throw new Error('Could not create voice note URL.');
    }

    return publicData.publicUrl;
  }, []);

  const sendVoiceNoteMessage = useCallback(
    async ({ audioBlob, durationSeconds, chatId, senderUsername, receiverUsername }) => {
      if (!audioBlob || !chatId || !senderUsername || !receiverUsername) return;

      setSendingMessage(true);
      setUploadingVoice(true);
      setVoiceError('');
      setMessagesError('');

      try {
        let mediaUrl = '';
        try {
          mediaUrl = await uploadVoiceNoteFile({ audioBlob, chatId, senderUsername });
        } catch (uploadError) {
          if (isRlsViolationError(uploadError)) {
            // Fallback when storage.objects policies cannot be managed in this project.
            mediaUrl = await blobToDataUrl(audioBlob);
          } else {
            throw uploadError;
          }
        }

        let { data, error } = await supabase
          .from('messages')
          .insert([
            {
              chat_request_id: chatId,
              sender_username: senderUsername,
              receiver_username: receiverUsername,
              message_text: 'Voice note',
              message_type: 'voice',
              media_url: mediaUrl,
              duration_seconds: durationSeconds,
              sent_at: new Date().toISOString(),
              is_read: false,
              status: 'sent',
            },
          ])
          .select('*')
          .single();

        if (error && isRlsViolationError(error)) {
          // Compatibility fallback for stricter text-only INSERT policies.
          ({ data, error } = await supabase
            .from('messages')
            .insert([
              {
                chat_request_id: chatId,
                sender_username: senderUsername,
                receiver_username: receiverUsername,
                message_text: 'Voice note',
                message_type: 'text',
                media_url: mediaUrl,
                duration_seconds: durationSeconds,
                sent_at: new Date().toISOString(),
                is_read: false,
                status: 'sent',
              },
            ])
            .select('*')
            .single());
        }

        if (error) {
          throw new Error(error.message || 'Could not send voice note.');
        }

        shouldStickToBottomRef.current = true;
        setChatMessages((prev) => {
          if (prev.some((message) => isSameChatId(message.id, data.id))) {
            return prev;
          }
          return [...prev, data];
        });
      } catch (error) {
        if (!isAbortError(error)) {
          // eslint-disable-next-line no-console
          console.error('Failed to send voice note:', error);
        }
        if (isRlsViolationError(error)) {
          setVoiceError('Voice note blocked by messages table policy.');
        } else {
          setVoiceError(error?.message || 'Could not send voice note.');
        }
      } finally {
        setUploadingVoice(false);
        setSendingMessage(false);
      }
    },
    [uploadVoiceNoteFile]
  );

  const startVoiceRecording = useCallback(async () => {
    if (sendingMessage || isRecordingVoice) return;
    if (!selectedChat?.id || !selectedChat?.otherUsername || !profile?.username) return;

    if (!supportsVoiceNotes) {
      setVoiceError('Voice notes are not supported in this browser.');
      return;
    }

    setVoiceError('');
    setMessagesError('');
    setShowEmojiPicker(false);

    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const preferredMimeTypes = [
        'audio/webm;codecs=opus',
        'audio/webm',
        'audio/mp4',
        'audio/ogg;codecs=opus',
      ];
      const canCheckMimeType = typeof window.MediaRecorder.isTypeSupported === 'function';
      const supportedMimeType = canCheckMimeType
        ? preferredMimeTypes.find((mimeType) => window.MediaRecorder.isTypeSupported(mimeType))
        : '';
      const mediaRecorder = supportedMimeType
        ? new window.MediaRecorder(stream, { mimeType: supportedMimeType })
        : new window.MediaRecorder(stream);

      const chatId = selectedChat.id;
      const senderUsername = profile.username;
      const receiverUsername = selectedChat.otherUsername;

      recordingStreamRef.current = stream;
      recordingMediaRecorderRef.current = mediaRecorder;
      recordingChunksRef.current = [];
      recordingStartedAtRef.current = Date.now();
      setRecordingSeconds(0);
      setIsRecordingVoice(true);

      mediaRecorder.ondataavailable = (event) => {
        if (event?.data && event.data.size > 0) {
          recordingChunksRef.current.push(event.data);
        }
      };

      mediaRecorder.onerror = () => {
        setVoiceError('Recording failed. Please try again.');
        discardVoiceRecording();
      };

      mediaRecorder.onstop = async () => {
        clearVoiceRecordingInterval();
        setIsRecordingVoice(false);
        setRecordingSeconds(0);

        const elapsedSeconds = Math.max(1, Math.round((Date.now() - recordingStartedAtRef.current) / 1000));
        const chunks = recordingChunksRef.current;
        recordingChunksRef.current = [];
        recordingStartedAtRef.current = 0;
        recordingMediaRecorderRef.current = null;
        releaseVoiceRecordingStream();

        const audioBlob = new Blob(chunks, { type: mediaRecorder.mimeType || 'audio/webm' });
        if (!audioBlob.size) {
          setVoiceError('Voice note is empty. Please try recording again.');
          return;
        }

        await sendVoiceNoteMessage({
          audioBlob,
          durationSeconds: elapsedSeconds,
          chatId,
          senderUsername,
          receiverUsername,
        });
      };

      clearVoiceRecordingInterval();
      recordingIntervalRef.current = window.setInterval(() => {
        setRecordingSeconds(Math.max(0, Math.round((Date.now() - recordingStartedAtRef.current) / 1000)));
      }, 1000);

      mediaRecorder.start(250);
    } catch (error) {
      releaseVoiceRecordingStream();
      recordingMediaRecorderRef.current = null;
      recordingChunksRef.current = [];
      recordingStartedAtRef.current = 0;
      clearVoiceRecordingInterval();
      setIsRecordingVoice(false);
      setRecordingSeconds(0);

      if (error?.name === 'NotAllowedError' || error?.name === 'SecurityError') {
        setVoiceError('Microphone permission denied.');
      } else {
        setVoiceError('Could not start voice recording.');
      }
    }
  }, [
    clearVoiceRecordingInterval,
    discardVoiceRecording,
    isRecordingVoice,
    profile?.username,
    releaseVoiceRecordingStream,
    selectedChat?.id,
    selectedChat?.otherUsername,
    sendVoiceNoteMessage,
    sendingMessage,
    supportsVoiceNotes,
  ]);

  const stopVoiceRecording = useCallback(() => {
    const recorder = recordingMediaRecorderRef.current;
    if (!recorder || recorder.state !== 'recording') return;

    clearVoiceRecordingInterval();
    setIsRecordingVoice(false);
    setRecordingSeconds(0);

    try {
      recorder.stop();
    } catch (error) {
      if (!isAbortError(error)) {
        setVoiceError('Could not stop recording.');
      }
      discardVoiceRecording();
    }
  }, [clearVoiceRecordingInterval, discardVoiceRecording]);

  const loadChatMessages = useCallback(async (chatToLoad, options = {}) => {
    if (!chatToLoad?.id || !profile?.username) return;

    const { silent = false } = options;
    if (!silent) {
      setLoadingMessages(true);
      setMessagesError('');
    }

    try {
      const { data, error } = await supabase
        .from('messages')
        .select(
          'id, chat_request_id, sender_username, receiver_username, message_text, message_type, media_url, duration_seconds, sent_at, is_read, status'
        )
        .eq('chat_request_id', chatToLoad.id)
        .order('sent_at', { ascending: true });

      if (error) {
        if (!silent) {
          setChatMessages([]);
          setMessagesError('Could not load messages right now.');
        }
        return;
      }

      const nextMessages = data || [];
      setChatMessages((prev) => (hasSameMessageSnapshot(prev, nextMessages) ? prev : nextMessages));
      await markChatAsRead(chatToLoad.id, profile.username);
      setActiveChats((prev) =>
        sortChatsByPriority(
          prev.map((chat) => (isSameChatId(chat.id, chatToLoad.id) ? { ...chat, unreadCount: 0 } : chat))
        )
      );
      setSelectedChat((prevSelected) =>
        isSameChatId(prevSelected?.id, chatToLoad.id) ? { ...prevSelected, unreadCount: 0 } : prevSelected
      );
    } catch (error) {
      if (isAbortError(error)) return;
      // eslint-disable-next-line no-console
      console.error('Failed to load chat messages:', error);
      if (!silent) {
        setChatMessages([]);
        setMessagesError('Could not load messages right now.');
      }
    } finally {
      if (!silent) {
        setLoadingMessages(false);
      }
    }
  }, [markChatAsRead, profile?.username, sortChatsByPriority]);

  const openChat = (chat) => {
    if (!chat) return;
    discardVoiceRecording();
    setSelectedChat({ ...chat, unreadCount: 0 });
    setActiveChats((prev) =>
      sortChatsByPriority(
        prev.map((item) => (item.id === chat.id ? { ...item, unreadCount: 0 } : item))
      )
    );
    setChatMessages([]);
    setMessagesError('');
    setNewMessage('');
    setVoiceError('');
    setChatOpen(true);
  };

  const closeChat = () => {
    discardVoiceRecording();
    setChatOpen(false);
    setSelectedChat(null);
    setChatMessages([]);
    setMessagesError('');
    setNewMessage('');
    setVoiceError('');
    setShowEmojiPicker(false);
  };

  const fetchProfile = async (user) => {
    try {
      const { data, error } = await supabase.from('credentials').select('*').eq('id', user.id).maybeSingle();
      if (error) return getFallbackProfile(user);
      if (!data || !hasValidProfileUsername(data)) {
        return { ...getPendingProfile(user), __requiresUsernameSetup: true };
      }
      return data;
    } catch (error) {
      if (isAbortError(error)) return null;
      // eslint-disable-next-line no-console
      console.error('Failed to fetch profile:', error);
      return getFallbackProfile(user);
    }
  };

  const fetchActiveChats = useCallback(async (currentProfile, options = {}) => {
    if (!currentProfile?.username) {
      setActiveChats([]);
      setChatsError('');
      return;
    }

    const { silent = false } = options;
    if (!silent) {
      setLoadingChats(true);
      setChatsError('');
    }

    try {
      const nowIso = new Date().toISOString();
      const { data, error } = await supabase
        .from('chat_requests')
        .select('id, fan_username, celebrity_username, chat_expiry_time, chat_start_time, payment_confirmed')
        .or(`fan_username.eq.${currentProfile.username},celebrity_username.eq.${currentProfile.username}`)
        .eq('payment_confirmed', true)
        .gte('chat_expiry_time', nowIso)
        .order('chat_start_time', { ascending: false });

      if (error) {
        setActiveChats([]);
        setChatsError('Could not load active chats right now.');
        return;
      }

      const mappedChats = (data || []).map((chat) => {
        const otherUsername =
          chat.fan_username === currentProfile.username ? chat.celebrity_username : chat.fan_username;

        return {
          ...chat,
          otherUsername,
          otherUserAvatar: null,
          otherUserGender: null,
          unreadCount: 0,
        };
      });

      const otherUsernames = [...new Set(mappedChats.map((chat) => chat.otherUsername).filter(Boolean))];
      let avatarMap = {};
      let unreadCountsMap = {};

      if (otherUsernames.length > 0) {
        const { data: profiles, error: profileError } = await supabase
          .from('credentials')
          .select('username, avatar_url, gender')
          .in('username', otherUsernames);

        if (!profileError && Array.isArray(profiles)) {
          avatarMap = profiles.reduce((acc, userProfile) => {
            acc[userProfile.username] = {
              avatarUrl: userProfile.avatar_url || null,
              gender: userProfile.gender || null,
            };
            return acc;
          }, {});
        }
      }

      const chatIds = mappedChats.map((chat) => chat.id).filter(Boolean);
      if (chatIds.length > 0) {
        const { data: unreadRows, error: unreadError } = await supabase
          .from('messages')
          .select('chat_request_id')
          .eq('receiver_username', currentProfile.username)
          .eq('is_read', false)
          .in('chat_request_id', chatIds);

        if (!unreadError && Array.isArray(unreadRows)) {
          unreadCountsMap = unreadRows.reduce((acc, row) => {
            const key = row.chat_request_id;
            acc[key] = (acc[key] || 0) + 1;
            return acc;
          }, {});
        }
      }

      const nextChats = mappedChats.map((chat) => ({
        ...chat,
        otherUserAvatar: avatarMap[chat.otherUsername]?.avatarUrl || null,
        otherUserGender: avatarMap[chat.otherUsername]?.gender || null,
        unreadCount: unreadCountsMap[chat.id] || 0,
      }));

      const currentUsername = currentProfile.username;
      if (activeChatNotificationUsernameRef.current !== currentUsername) {
        activeChatNotificationUsernameRef.current = currentUsername;
        knownActiveChatIdsRef.current = new Set(nextChats.map((chat) => String(chat.id)));
      } else {
        nextChats.forEach((chat) => {
          const chatId = String(chat.id);
          const isNewlyActiveChat = !knownActiveChatIdsRef.current.has(chatId);
          knownActiveChatIdsRef.current.add(chatId);

          if (!isNewlyActiveChat || notifiedUnlockChatIdsRef.current.has(chatId)) return;
          notifiedUnlockChatIdsRef.current.add(chatId);

          void showBrowserNotification({
            title: 'Chat unlocked',
            body: `Your 24-hour chat with ${chat.otherUsername || 'an Unlock user'} is now active.`,
            tag: `unlock-chat-${chatId}`,
            url: `/app?chat=${encodeURIComponent(chatId)}`,
          });
        });
      }

      if (unreadNotificationUsernameRef.current !== currentUsername) {
        unreadNotificationUsernameRef.current = currentUsername;
        knownUnreadCountsRef.current = new Map(
          nextChats.map((chat) => [String(chat.id), Number(chat.unreadCount || 0)])
        );
      } else {
        nextChats.forEach((chat) => {
          const chatId = String(chat.id);
          const nextUnreadCount = Number(chat.unreadCount || 0);
          const previousUnreadCount = Number(knownUnreadCountsRef.current.get(chatId) || 0);

          if (nextUnreadCount > previousUnreadCount) {
            const newMessageCount = nextUnreadCount - previousUnreadCount;
            void showBrowserNotification({
              title: `New message from ${chat.otherUsername || 'Unlock user'}`,
              body:
                newMessageCount === 1
                  ? 'Sent you a new message.'
                  : `Sent you ${newMessageCount} new messages.`,
              tag: `message-chat-${chatId}`,
              url: `/app?chat=${encodeURIComponent(chatId)}`,
            });
          }

          knownUnreadCountsRef.current.set(chatId, nextUnreadCount);
        });
      }

      setActiveChats(sortChatsByPriority(nextChats));
      setSelectedChat((prevSelected) => {
        if (!prevSelected?.id) return prevSelected;
        const updatedSelected = nextChats.find((chat) => chat.id === prevSelected.id);
        if (!updatedSelected) return prevSelected;
        return { ...prevSelected, ...updatedSelected };
      });
    } catch (error) {
      if (isAbortError(error)) {
        if (!silent) {
          setLoadingChats(false);
        }
        return;
      }
      // eslint-disable-next-line no-console
      console.error('Failed to load active chats:', error);
      setActiveChats([]);
      setChatsError('Could not load active chats right now.');
    } finally {
      if (!silent) {
        setLoadingChats(false);
      }
    }
  }, [sortChatsByPriority]);

  const refreshActiveChats = () => {
    if (!profile) return;
    fetchActiveChats(profile, { silent: false });
  };

  const handleUserSearch = async (event) => {
    event.preventDefault();

    if (!profile?.username) return;

    const cleanQuery = searchQuery.trim();
    setHasSearchedUsers(true);

    if (!cleanQuery) {
      setSearchResults([]);
      setSearchError('Enter a username to search.');
      return;
    }

    setSearchingUsers(true);
    setSearchError('');

    try {
      const { data, error } = await supabase
        .from('credentials')
        .select('id, username, avatar_url, user_rate, chat_preference, gender, user_rating, total_rating, rating_count')
        .ilike('username', `%${cleanQuery}%`)
        .neq('username', profile.username)
        .order('username', { ascending: true })
        .limit(20);

      if (error) {
        setSearchResults([]);
        setSearchError('Could not search users right now.');
        return;
      }

      setSearchResults(data || []);
    } catch (error) {
      if (isAbortError(error)) return;
      // eslint-disable-next-line no-console
      console.error('User search failed:', error);
      setSearchResults([]);
      setSearchError('Could not search users right now.');
    } finally {
      setSearchingUsers(false);
    }
  };

  const closeUnlockModal = useCallback(() => {
    if (paymentProofPreview?.startsWith('blob:')) {
      URL.revokeObjectURL(paymentProofPreview);
    }
    setUnlockModalOpen(false);
    setCurrentUnlockTarget(null);
    setTargetInstapay('');
    setPaymentProofFile(null);
    setPaymentProofPreview('');
    setSubmittingPaymentProof(false);
    setUnlockError('');
  }, [paymentProofPreview]);

  const handlePaymentProofSelection = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!file.type?.startsWith('image/')) {
      setUnlockError('Please select an image file for payment proof.');
      return;
    }

    if (paymentProofPreview?.startsWith('blob:')) {
      URL.revokeObjectURL(paymentProofPreview);
    }

    setPaymentProofFile(file);
    setPaymentProofPreview(URL.createObjectURL(file));
    setUnlockError('');
    event.target.value = '';
  };

  const getUserTransactionTotalLast30Days = useCallback(async (username) => {
    const thirtyDaysAgo = new Date();
    thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);

    const { data, error } = await supabase
      .from('chat_requests')
      .select('amount_paid')
      .or(`fan_username.eq.${username},celebrity_username.eq.${username}`)
      .eq('payment_confirmed', true)
      .gte('created_at', thirtyDaysAgo.toISOString());

    if (error) {
      throw new Error(error.message || 'Failed to check transaction limits.');
    }

    return (data || []).reduce((sum, chat) => sum + (chat.amount_paid || 0), 0);
  }, []);

  const checkExistingChatRequest = useCallback(async (fanUsername, celebrityUsername) => {
    try {
      const { data, error } = await supabase
        .from('chat_requests')
        .select('id, payment_confirmed, chat_expiry_time')
        .eq('fan_username', fanUsername)
        .eq('celebrity_username', celebrityUsername);

      if (error) return false;
      if (!data || data.length === 0) return false;

      const now = new Date();
      for (const request of data) {
        if (!request.payment_confirmed) return true;
        if (request.payment_confirmed && request.chat_expiry_time) {
          const expiryTime = new Date(request.chat_expiry_time);
          if (expiryTime > now) return true;
        }
      }
      return false;
    } catch (error) {
      return false;
    }
  }, []);

  const uploadPaymentProof = useCallback(async (file, userId, targetUsername) => {
    const supabaseUrl = process.env.REACT_APP_SUPABASE_URL;
    const supabaseAnonKey = process.env.REACT_APP_SUPABASE_ANON_KEY;

    if (!supabaseUrl || !supabaseAnonKey) {
      throw new Error('Missing Supabase configuration for payment proof upload.');
    }

    const fileExt = file.name.split('.').pop()?.toLowerCase() || 'jpg';
    const fileName = `${userId}-${targetUsername}-${Date.now()}.${fileExt}`;
    const filePath = `payment-proofs/${fileName}`;

    const formData = new FormData();
    formData.append('file', file, fileName);

    const uploadResponse = await fetch(`${supabaseUrl}/storage/v1/object/payment-proofs/${filePath}`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${supabaseAnonKey}`,
        apikey: supabaseAnonKey,
      },
      body: formData,
    });

    if (!uploadResponse.ok) {
      const errorText = await uploadResponse.text();
      throw new Error(errorText || `Failed to upload payment proof (${uploadResponse.status}).`);
    }

    return `${supabaseUrl}/storage/v1/object/public/payment-proofs/${filePath}`;
  }, []);

  const getUnlockRestrictionReason = useCallback((targetUser) => {
    if (!targetUser) return 'Invalid target user.';
    if (activeChats.length >= 10) {
      return 'You have reached the maximum of 10 concurrent chats.';
    }
    if (activeChatPartners.has(targetUser.username)) {
      return `You already have an active chat with ${targetUser.username}.`;
    }

    const targetPreference = targetUser.chat_preference || 'both';
    const currentUserGender = profile?.gender || 'male';
    if (targetPreference === 'none') {
      return `${targetUser.username} is not accepting new requests right now.`;
    }
    if (targetPreference === 'male' && currentUserGender !== 'male') {
      return `${targetUser.username} accepts requests from males only.`;
    }
    if (targetPreference === 'female' && currentUserGender !== 'female') {
      return `${targetUser.username} accepts requests from females only.`;
    }

    return '';
  }, [activeChatPartners, activeChats.length, profile?.gender]);

  const handleUnlockClick = useCallback(
    async (targetUser) => {
      if (!profile?.username || !profile?.id || !targetUser?.username) return;

      const localRestriction = getUnlockRestrictionReason(targetUser);
      if (localRestriction) {
        window.alert(localRestriction);
        return;
      }

      setUnlockingUsername(targetUser.username);
      setUnlockError('');

      try {
        const myTotalLast30Days = await getUserTransactionTotalLast30Days(profile.username);
        if (myTotalLast30Days >= 400000) {
          window.alert(
            'You reached the monthly transaction limit of 400,000 EGP in the last 30 days. You cannot unlock new chats right now.'
          );
          return;
        }

        const hasExistingRequest = await checkExistingChatRequest(profile.username, targetUser.username);
        if (hasExistingRequest) {
          window.alert(
            `You already have a pending or active request with ${targetUser.username}. Please resolve the current one before trying again.`
          );
          return;
        }

        const { data: celebrityData, error: celebrityError } = await supabase
          .from('credentials')
          .select('user_instapay')
          .eq('username', targetUser.username)
          .maybeSingle();

        if (celebrityError || !celebrityData?.user_instapay) {
          window.alert(`${targetUser.username} has not set up Instapay yet. Please try another user later.`);
          return;
        }

        const nowIso = new Date().toISOString();
        const { count: celebrityActiveChatsCount, error: countError } = await supabase
          .from('chat_requests')
          .select('id', { count: 'exact', head: true })
          .or(`fan_username.eq.${targetUser.username},celebrity_username.eq.${targetUser.username}`)
          .eq('payment_confirmed', true)
          .gte('chat_expiry_time', nowIso);

        if (!countError && (celebrityActiveChatsCount || 0) >= 10) {
          window.alert(`${targetUser.username} reached the maximum limit of 10 active chats. Please try again later.`);
          return;
        }

        try {
          const targetTotalLast30Days = await getUserTransactionTotalLast30Days(targetUser.username);
          if (targetTotalLast30Days >= 400000) {
            window.alert(`${targetUser.username} reached their monthly transaction limit. Please choose another user.`);
            return;
          }
        } catch (error) {
          // eslint-disable-next-line no-console
          console.error('Failed to check celebrity transaction cap:', error);
        }

        const targetRate = targetUser.user_rate || 100;
        const confirmed = window.confirm(
          `Unlock a 24-hour chat portal with ${targetUser.username} for ${targetRate} EGP?\n\nNote: ${targetUser.username} is not obligated to reply to your messages.`
        );
        if (!confirmed) return;

        closeUnlockModal();
        setCurrentUnlockTarget(targetUser);
        setTargetInstapay(celebrityData.user_instapay);
        setUnlockModalOpen(true);
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Failed to start unlock flow:', error);
        window.alert(error?.message || 'Failed to verify unlock requirements. Please try again.');
      } finally {
        setUnlockingUsername('');
      }
    },
    [
      checkExistingChatRequest,
      closeUnlockModal,
      getUnlockRestrictionReason,
      getUserTransactionTotalLast30Days,
      profile?.id,
      profile?.username,
    ]
  );

  const submitPaymentProof = async () => {
    if (!profile?.id || !profile?.username || !currentUnlockTarget || !paymentProofFile) {
      setUnlockError('Please select a payment proof image first.');
      return;
    }

    setSubmittingPaymentProof(true);
    setUnlockError('');

    try {
      const myTotalLast30Days = await getUserTransactionTotalLast30Days(profile.username);
      if (myTotalLast30Days >= 400000) {
        setUnlockError('You reached the monthly transaction limit of 400,000 EGP.');
        return;
      }

      const hasExistingRequest = await checkExistingChatRequest(profile.username, currentUnlockTarget.username);
      if (hasExistingRequest) {
        setUnlockError(
          `You already have a pending or active request with ${currentUnlockTarget.username}.`
        );
        return;
      }

      const paymentProofUrl = await uploadPaymentProof(
        paymentProofFile,
        profile.id,
        currentUnlockTarget.username
      );

      const { data: celebrityData } = await supabase
        .from('credentials')
        .select('user_instapay')
        .eq('username', currentUnlockTarget.username)
        .maybeSingle();

      const targetRate = currentUnlockTarget.user_rate || 100;
      const { error } = await supabase.from('chat_requests').insert([
        {
          fan_username: profile.username,
          celebrity_username: currentUnlockTarget.username,
          amount_paid: targetRate,
          payment_confirmed: false,
          chat_rating: null,
          celebrity_instapay: celebrityData?.user_instapay || targetInstapay || '',
          payment_proof: paymentProofUrl,
        },
      ]);

      if (error) {
        throw new Error(error.message || 'Failed to create chat request.');
      }

      window.alert(
        `Payment proof uploaded. Your ${targetRate} EGP request with ${currentUnlockTarget.username} is pending verification.`
      );

      closeUnlockModal();
      await fetchActiveChats(profile, { silent: false });
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Failed to submit payment proof:', error);
      setUnlockError(error?.message || 'Failed to submit payment proof. Please try again.');
    } finally {
      setSubmittingPaymentProof(false);
    }
  };

  const handleSendMessage = async (event) => {
    event.preventDefault();

    if (!selectedChat || !profile?.username || isRecordingVoice) return;

    const trimmed = newMessage.trim();
    if (!trimmed) return;

    const receiverUsername = selectedChat.otherUsername;
    if (!receiverUsername) return;

    setSendingMessage(true);
    setVoiceError('');

    try {
      const { data, error } = await supabase
        .from('messages')
        .insert([
          {
            chat_request_id: selectedChat.id,
            sender_username: profile.username,
            receiver_username: receiverUsername,
            message_text: trimmed,
            message_type: 'text',
            media_url: null,
            duration_seconds: null,
            sent_at: new Date().toISOString(),
            is_read: false,
            status: 'sent',
          },
        ])
        .select('*')
        .single();

      if (error) {
        setMessagesError('Could not send message.');
        return;
      }

      shouldStickToBottomRef.current = true;
      setChatMessages((prev) => {
        if (prev.some((message) => message.id === data.id)) {
          return prev;
        }
        return [...prev, data];
      });
      setNewMessage('');
      setShowEmojiPicker(false);
    } catch (error) {
      if (!isAbortError(error)) {
        // eslint-disable-next-line no-console
        console.error('Failed to send message:', error);
      }
      setMessagesError('Could not send message.');
    } finally {
      setSendingMessage(false);
    }
  };

  const insertEmoji = (emoji) => {
    setNewMessage((prev) => `${prev}${emoji}`);
  };

  const syncSettingsFromProfile = useCallback((targetProfile) => {
    if (!targetProfile) return;
    setUserRate(targetProfile.user_rate || 100);
    setUserChatPreference(targetProfile.chat_preference || 'both');
    setUserInstapay(targetProfile.user_instapay || '');
    setAvatarPreview(targetProfile.avatar_url || '');
    setSelectedAvatarFile(null);
    setSettingsAlert({ type: '', message: '' });
  }, []);

  const openSettingsModal = () => {
    if (!profile) return;
    syncSettingsFromProfile(profile);
    setNotificationPermission(getBrowserNotificationPermission());
    setSettingsTab('general');
    setSettingsOpen(true);
  };

  const closeSettingsModal = () => {
    setSettingsOpen(false);
    setSettingsAlert({ type: '', message: '' });
  };

  const handleEnableNotifications = async () => {
    if (!isBrowserNotificationSupported()) {
      setSettingsAlert({
        type: 'error',
        message: 'Notifications are not supported by this browser.',
      });
      return;
    }

    setRequestingNotificationPermission(true);
    setSettingsAlert({ type: '', message: '' });

    try {
      const permission = await requestBrowserNotificationPermission();
      setNotificationPermission(permission);

      if (permission === 'granted') {
        setSettingsAlert({
          type: 'success',
          message: 'Notifications are enabled for new messages and unlocked chats.',
        });
      } else if (permission === 'denied') {
        setSettingsAlert({
          type: 'error',
          message: 'Notifications are blocked. Enable them for this site in your browser settings.',
        });
      } else {
        setSettingsAlert({ type: 'error', message: 'Notification permission was not granted.' });
      }
    } catch (_error) {
      setSettingsAlert({ type: 'error', message: 'Could not enable notifications. Please try again.' });
    } finally {
      setRequestingNotificationPermission(false);
    }
  };

  const handleAvatarChange = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!file.type?.startsWith('image/')) {
      setSettingsAlert({ type: 'error', message: 'Please select a valid image file.' });
      return;
    }

    setSelectedAvatarFile(file);
    setAvatarPreview(URL.createObjectURL(file));
    setSettingsAlert({ type: '', message: '' });
    event.target.value = '';
  };

  const handleSaveGeneralSettings = async () => {
    if (!profile?.id) return;

    setSavingGeneral(true);
    setSettingsAlert({ type: '', message: '' });

    let uploadedAvatarPath = '';
    let shouldCleanupUploadedAvatar = false;

    try {
      let nextAvatarUrl = profile.avatar_url || null;
      const previousAvatarPaths = getStorageObjectPathCandidates(profile.avatar_url, 'avatars');
      let shouldDeletePreviousAvatar = false;

      if (selectedAvatarFile) {
        const fileExt = selectedAvatarFile.name.split('.').pop()?.toLowerCase() || 'jpg';
        const fileName = `${profile.id}-${Date.now()}.${fileExt}`;
        const filePath = `avatars/${fileName}`;
        uploadedAvatarPath = filePath;
        shouldCleanupUploadedAvatar = true;

        const { error: uploadError } = await supabase.storage
          .from('avatars')
          .upload(filePath, selectedAvatarFile, {
            cacheControl: '3600',
            upsert: false,
            contentType: selectedAvatarFile.type || undefined,
          });

        if (uploadError) {
          throw new Error(`Failed to upload avatar: ${uploadError.message}`);
        }

        const { data: publicData } = supabase.storage.from('avatars').getPublicUrl(filePath);
        if (publicData?.publicUrl) {
          nextAvatarUrl = publicData.publicUrl;
          const nextPathCandidates = new Set(getStorageObjectPathCandidates(filePath, 'avatars'));
          shouldDeletePreviousAvatar = previousAvatarPaths.some((path) => !nextPathCandidates.has(path));
        } else {
          throw new Error('Failed to generate avatar URL after upload.');
        }
      }

      const updates = {
        user_rate: userRate,
        chat_preference: userChatPreference,
        avatar_url: nextAvatarUrl,
      };

      const { error } = await supabase.from('credentials').update(updates).eq('id', profile.id);
      if (error) {
        throw new Error(error.message || 'Failed to save settings.');
      }

      shouldCleanupUploadedAvatar = false;
      setProfile((prev) => (prev ? { ...prev, ...updates } : prev));
      setSettingsOpen(false);

      if (shouldDeletePreviousAvatar) {
        const nextPathCandidates = new Set(getStorageObjectPathCandidates(nextAvatarUrl, 'avatars'));
        const pathsToDelete = previousAvatarPaths.filter((path) => !nextPathCandidates.has(path));

        if (pathsToDelete.length > 0) {
          const { error: removeError } = await supabase.storage.from('avatars').remove(pathsToDelete);
          if (removeError) {
            // eslint-disable-next-line no-console
            console.warn('Previous avatar was not deleted:', removeError, { pathsToDelete });
            setSettingsAlert({
              type: 'success',
              message: 'Profile updated, but old avatar cleanup was blocked by storage permissions.',
            });
          }
        }
      }
    } catch (error) {
      if (shouldCleanupUploadedAvatar && uploadedAvatarPath) {
        const uploadedPathCandidates = getStorageObjectPathCandidates(uploadedAvatarPath, 'avatars');
        const { error: cleanupError } = await supabase.storage.from('avatars').remove(uploadedPathCandidates);
        if (cleanupError) {
          // eslint-disable-next-line no-console
          console.warn('New avatar cleanup failed after save error:', cleanupError);
        }
      }
      // eslint-disable-next-line no-console
      console.error('Failed to save general settings:', error);
      setSettingsAlert({
        type: 'error',
        message: error?.message || 'Failed to save profile settings.',
      });
    } finally {
      setSavingGeneral(false);
    }
  };

  const handleSaveInstapay = async () => {
    if (!profile?.id) return;

    const normalizedInstapay = userInstapay.trim();
    const phoneRegex = /^(01)[0-9]{9}$/;

    if (!normalizedInstapay) {
      setSettingsAlert({ type: 'error', message: 'Please enter your Instapay number.' });
      setSettingsTab('payment');
      return;
    }

    if (!phoneRegex.test(normalizedInstapay)) {
      setSettingsAlert({
        type: 'error',
        message: 'Enter a valid Egyptian phone number (01XXXXXXXXX).',
      });
      setSettingsTab('payment');
      return;
    }

    setSavingPayment(true);
    setSettingsAlert({ type: '', message: '' });

    try {
      const { error } = await supabase
        .from('credentials')
        .update({ user_instapay: normalizedInstapay })
        .eq('id', profile.id);

      if (error) {
        throw new Error(error.message || 'Failed to save Instapay.');
      }

      setProfile((prev) => (prev ? { ...prev, user_instapay: normalizedInstapay } : prev));
      setSettingsAlert({ type: 'success', message: 'Instapay number saved successfully.' });
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Failed to save Instapay settings:', error);
      setSettingsAlert({
        type: 'error',
        message: error?.message || 'Failed to save Instapay number.',
      });
    } finally {
      setSavingPayment(false);
    }
  };

  const removeUnratedChatFromQueue = useCallback((chatIdToRemove) => {
    if (!chatIdToRemove) return;

    setUnratedChats((prev) => {
      const remaining = prev.filter((chat) => !isSameChatId(chat.id, chatIdToRemove));
      const nextChat = remaining[0] || null;
      setCurrentRatingChat(nextChat);
      setRatingModalOpen(Boolean(nextChat));
      setSelectedRating(null);
      setRatingError('');
      return remaining;
    });
  }, []);

  const fetchUnratedChats = useCallback(async () => {
    if (!profile?.username) return;

    try {
      const nowIso = new Date().toISOString();
      const { data, error } = await supabase
        .from('chat_requests')
        .select('id, celebrity_username, chat_expiry_time, created_at')
        .eq('fan_username', profile.username)
        .lt('chat_expiry_time', nowIso)
        .is('chat_rating', null)
        .order('created_at', { ascending: false });

      if (error) return;

      const pendingChats = (data || []).filter(
        (chat) => !skippedRatingChatIdsRef.current.has(String(chat.id))
      );

      if (pendingChats.length === 0) {
        setUnratedChats([]);
        setCurrentRatingChat(null);
        setRatingModalOpen(false);
        setSelectedRating(null);
        setRatingError('');
        return;
      }

      const celebrityUsernames = [
        ...new Set(pendingChats.map((chat) => chat.celebrity_username).filter(Boolean)),
      ];
      let avatarMap = {};

      if (celebrityUsernames.length > 0) {
        const { data: avatarRows, error: avatarError } = await supabase
          .from('credentials')
          .select('username, avatar_url')
          .in('username', celebrityUsernames);

        if (!avatarError && Array.isArray(avatarRows)) {
          avatarMap = avatarRows.reduce((acc, row) => {
            acc[row.username] = row.avatar_url || null;
            return acc;
          }, {});
        }
      }

      const chatsWithAvatars = pendingChats.map((chat) => ({
        ...chat,
        celebrityAvatar: avatarMap[chat.celebrity_username] || null,
      }));

      const preservedCurrent = currentRatingChat?.id
        ? chatsWithAvatars.find((chat) => isSameChatId(chat.id, currentRatingChat.id))
        : null;
      const nextCurrent = preservedCurrent || chatsWithAvatars[0] || null;

      setUnratedChats(chatsWithAvatars);
      setCurrentRatingChat(nextCurrent);
      setRatingModalOpen(Boolean(nextCurrent));
      setSelectedRating((prev) =>
        currentRatingChat?.id && nextCurrent && isSameChatId(currentRatingChat.id, nextCurrent.id) ? prev : null
      );
      setRatingError('');
    } catch (error) {
      if (!isAbortError(error)) {
        // eslint-disable-next-line no-console
        console.error('Failed to check unrated chats:', error);
      }
    }
  }, [currentRatingChat?.id, profile?.username]);

  const handleSubmitRating = useCallback(async () => {
    if (!currentRatingChat?.id || !currentRatingChat?.celebrity_username) {
      setRatingError('Could not submit rating for this chat.');
      return;
    }

    if (selectedRating === null || selectedRating === undefined) {
      setRatingError('Please choose a rating before submitting, or press Skip.');
      return;
    }

    setSubmittingRating(true);
    setRatingError('');

    try {
      const { error: rpcError } = await supabase.rpc('submit_rating', {
        p_target_username: currentRatingChat.celebrity_username,
        p_rating: selectedRating,
      });

      if (rpcError) {
        throw new Error(rpcError.message || 'Failed to submit rating.');
      }

      const { error: updateError } = await supabase
        .from('chat_requests')
        .update({ chat_rating: selectedRating })
        .eq('id', currentRatingChat.id);

      if (updateError) {
        // eslint-disable-next-line no-console
        console.warn('Rating submitted but chat row update failed:', updateError);
      }

      removeUnratedChatFromQueue(currentRatingChat.id);
      await fetchActiveChats(profile, { silent: true });
    } catch (error) {
      if (!isAbortError(error)) {
        // eslint-disable-next-line no-console
        console.error('Failed to submit rating:', error);
      }
      setRatingError(error?.message || 'Failed to submit rating. Please try again.');
    } finally {
      setSubmittingRating(false);
    }
  }, [currentRatingChat, fetchActiveChats, profile, removeUnratedChatFromQueue, selectedRating]);

  const handleSkipRating = useCallback(() => {
    if (!currentRatingChat?.id) return;
    skippedRatingChatIdsRef.current.add(String(currentRatingChat.id));
    removeUnratedChatFromQueue(currentRatingChat.id);
  }, [currentRatingChat?.id, removeUnratedChatFromQueue]);

  const handleLogout = async () => {
    if (!hasSupabaseConfig) {
      navigate('/', { replace: true });
      return;
    }

    try {
      await supabase.auth.signOut();
    } catch (error) {
      if (!isAbortError(error)) {
        // eslint-disable-next-line no-console
        console.error('Sign out failed:', error);
      }
    }
    navigate('/', { replace: true });
  };

  const handleUsernameSetupSubmit = async (event) => {
    event.preventDefault();
    if (!pendingAuthUser?.id) return;

    const cleanUsername = usernameDraft.trim().toLowerCase();
    const usernameError = validateUsername(cleanUsername);
    if (usernameError) {
      setUsernameSetupError(usernameError);
      return;
    }

    setSavingUsernameSetup(true);
    setUsernameSetupError('');

    try {
      const { data: existingUsername, error: usernameCheckError } = await supabase
        .from('credentials')
        .select('id')
        .eq('username', cleanUsername)
        .maybeSingle();

      if (usernameCheckError) {
        setUsernameSetupError('Failed to validate username. Please try again.');
        return;
      }

      if (existingUsername && String(existingUsername.id) !== String(pendingAuthUser.id)) {
        setUsernameSetupError('Username already exists.');
        return;
      }

      const normalizedEmail = (pendingAuthUser.email || '').trim().toLowerCase();
      const avatarUrl =
        pendingAuthUser.user_metadata?.avatar_url || pendingAuthUser.user_metadata?.picture || null;

      const { data: existingProfile, error: existingProfileError } = await supabase
        .from('credentials')
        .select('id')
        .eq('id', pendingAuthUser.id)
        .maybeSingle();

      if (existingProfileError) {
        setUsernameSetupError('Could not prepare your profile. Please try again.');
        return;
      }

      if (existingProfile) {
        const { error: updateError } = await supabase
          .from('credentials')
          .update({
            username: cleanUsername,
            email: normalizedEmail,
            avatar_url: avatarUrl,
          })
          .eq('id', pendingAuthUser.id);

        if (updateError) {
          setUsernameSetupError(updateError.message || 'Failed to save username.');
          return;
        }
      } else {
        const { error: insertError } = await supabase.from('credentials').insert([
          {
            id: pendingAuthUser.id,
            username: cleanUsername,
            email: normalizedEmail,
            gender: 'male',
            user_rate: 100,
            chat_preference: 'both',
            avatar_url: avatarUrl,
            user_rating: null,
            user_instapay: null,
          },
        ]);

        if (insertError) {
          setUsernameSetupError(insertError.message || 'Failed to save username.');
          return;
        }
      }

      const { data: savedProfile, error: fetchSavedProfileError } = await supabase
        .from('credentials')
        .select('*')
        .eq('id', pendingAuthUser.id)
        .maybeSingle();

      if (fetchSavedProfileError) {
        setProfile({
          ...getPendingProfile(pendingAuthUser),
          username: cleanUsername,
          email: normalizedEmail,
          avatar_url: avatarUrl,
          gender: 'male',
          user_rate: 100,
          chat_preference: 'both',
          user_rating: null,
          user_instapay: null,
        });
      } else {
        setProfile(
          savedProfile || {
            ...getPendingProfile(pendingAuthUser),
            username: cleanUsername,
            email: normalizedEmail,
            avatar_url: avatarUrl,
            gender: 'male',
            user_rate: 100,
            chat_preference: 'both',
            user_rating: null,
            user_instapay: null,
          }
        );
      }

      setPendingAuthUser(null);
      setUsernameDraft('');
      setUsernameSetupError('');
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Failed to complete username setup:', error);
      setUsernameSetupError('Failed to save username. Please try again.');
    } finally {
      setSavingUsernameSetup(false);
    }
  };

  useEffect(() => {
    document.title = 'Unlock App | Active Chats Dashboard';

    const descriptionTag = document.querySelector('meta[name="description"]');
    if (descriptionTag) {
      descriptionTag.setAttribute(
        'content',
        'Unlock App dashboard for managing paid chats, messages, and creator settings.'
      );
    }

    const robotsTag = document.querySelector('meta[name="robots"]');
    if (robotsTag) {
      robotsTag.setAttribute('content', 'noindex, nofollow');
    }

    const canonicalTag = document.querySelector('link[rel="canonical"]');
    if (canonicalTag) {
      canonicalTag.setAttribute('href', 'https://unlockapp.me/app');
    }
  }, []);

  useEffect(() => {
    let mounted = true;
    let sessionCheckCompleted = false;
    let bootstrapTimeoutId = null;

    const completeSessionCheck = () => {
      if (!mounted) return;
      if (!sessionCheckCompleted) {
        sessionCheckCompleted = true;
        setIsCheckingSession(false);
      }
      if (bootstrapTimeoutId) {
        window.clearTimeout(bootstrapTimeoutId);
        bootstrapTimeoutId = null;
      }
    };

    const applyAuthenticatedSession = async (user) => {
      if (!mounted || !user) return;

      const fallbackProfile = getFallbackProfile(user);
      setProfile((previousProfile) => {
        if (!previousProfile || String(previousProfile.id) !== String(fallbackProfile.id)) {
          return fallbackProfile;
        }
        return { ...fallbackProfile, ...previousProfile };
      });
      completeSessionCheck();

      try {
        const nextProfile = await withTimeout(
          fetchProfile(user),
          PROFILE_BOOTSTRAP_TIMEOUT_MS,
          'Profile bootstrap timed out'
        );
        if (!mounted || !nextProfile) return;

        if (nextProfile.__requiresUsernameSetup) {
          setPendingAuthUser(user);
          setUsernameDraft(buildUsernameSuggestion(user));
          setUsernameSetupError('');
          setProfile(getPendingProfile(user));
          return;
        }

        setPendingAuthUser(null);
        setUsernameSetupError('');
        setUsernameDraft('');
        setProfile(nextProfile);
      } catch (error) {
        if (!mounted || isAbortError(error)) return;
        // eslint-disable-next-line no-console
        console.error('Profile bootstrap fallback used:', error);
      }
    };

    if (!hasSupabaseConfig) {
      completeSessionCheck();
      navigate('/', { replace: true });
      return undefined;
    }

    bootstrapTimeoutId = window.setTimeout(() => {
      if (!mounted || sessionCheckCompleted) return;
      completeSessionCheck();
      navigate('/', { replace: true });
    }, SESSION_BOOTSTRAP_TIMEOUT_MS);

    const init = async () => {
      try {
        const {
          data: { session },
        } = await withTimeout(
          supabase.auth.getSession(),
          AUTH_SESSION_LOOKUP_TIMEOUT_MS,
          'Session lookup timed out'
        );

        if (!mounted) return;

        if (!session?.user) {
          completeSessionCheck();
          navigate('/', { replace: true });
          return;
        }

        void applyAuthenticatedSession(session.user);
      } catch (error) {
        if (!mounted) return;
        if (!isAbortError(error)) {
          // eslint-disable-next-line no-console
          console.error('Session check failed:', error);
        }
        completeSessionCheck();
        navigate('/', { replace: true });
      }
    };

    void init();

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, session) => {
      if (!mounted) return;

      if (!session?.user) {
        setProfile(null);
        setActiveChats([]);
        setPendingAuthUser(null);
        setUsernameDraft('');
        setUsernameSetupError('');
        setSavingUsernameSetup(false);
        completeSessionCheck();
        navigate('/', { replace: true });
        return;
      }

      void applyAuthenticatedSession(session.user);
    });

    return () => {
      mounted = false;
      if (bootstrapTimeoutId) {
        window.clearTimeout(bootstrapTimeoutId);
      }
      subscription.unsubscribe();
    };
  }, [navigate]);

  useEffect(() => {
    if (!profile) return;
    fetchActiveChats(profile, { silent: false });
  }, [profile, fetchActiveChats]);

  useEffect(() => {
    if (!copyProfileLinkState) return undefined;
    const timeoutId = window.setTimeout(() => setCopyProfileLinkState(''), 2200);
    return () => window.clearTimeout(timeoutId);
  }, [copyProfileLinkState]);

  useEffect(() => {
    if (!profile?.username || !requestedUnlockUsername) return;

    const attemptKey = `${profile.username}:${requestedUnlockUsername.toLowerCase()}`;
    if (attemptedAutoUnlockKeyRef.current === attemptKey) return;
    attemptedAutoUnlockKeyRef.current = attemptKey;

    const autoStartUnlockFlow = async () => {
      try {
        const { data, error } = await supabase
          .from('credentials')
          .select('id, username, avatar_url, user_rate, chat_preference, gender, user_rating, total_rating, rating_count')
          .eq('username', requestedUnlockUsername)
          .maybeSingle();

        if (error || !data) {
          window.alert(`Profile @${requestedUnlockUsername} was not found.`);
          return;
        }

        if (data.username === profile.username) {
          window.alert('You cannot unlock your own profile.');
          return;
        }

        await handleUnlockClick(data);
      } catch (error) {
        if (!isAbortError(error)) {
          // eslint-disable-next-line no-console
          console.error('Failed to open shared profile unlock flow:', error);
        }
      }
    };

    void autoStartUnlockFlow();
  }, [handleUnlockClick, profile?.username, requestedUnlockUsername]);

  useEffect(() => {
    skippedRatingChatIdsRef.current = new Set();
    notifiedUnlockChatIdsRef.current = new Set();
    knownActiveChatIdsRef.current = new Set();
    activeChatNotificationUsernameRef.current = '';
    knownUnreadCountsRef.current = new Map();
    unreadNotificationUsernameRef.current = '';
    notifiedMessageIdsRef.current = new Set();
    setUnratedChats([]);
    setRatingModalOpen(false);
    setCurrentRatingChat(null);
    setSelectedRating(null);
    setSubmittingRating(false);
    setRatingError('');
  }, [profile?.id]);

  useEffect(() => {
    if (!profile?.username) return undefined;

    const refreshUnratedChats = () => {
      void fetchUnratedChats();
    };

    refreshUnratedChats();
    const intervalId = window.setInterval(refreshUnratedChats, 15000);
    const onWindowFocus = () => refreshUnratedChats();
    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        refreshUnratedChats();
      }
    };

    window.addEventListener('focus', onWindowFocus);
    document.addEventListener('visibilitychange', onVisibilityChange);

    return () => {
      window.clearInterval(intervalId);
      window.removeEventListener('focus', onWindowFocus);
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, [fetchUnratedChats, profile?.username]);

  useEffect(() => {
    if (!profile?.username) return undefined;

    const refreshSilently = () => {
      fetchActiveChats({ username: profile.username }, { silent: true });
    };

    const intervalId = window.setInterval(refreshSilently, 10000);
    const onWindowFocus = () => refreshSilently();
    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        refreshSilently();
      }
    };

    window.addEventListener('focus', onWindowFocus);
    document.addEventListener('visibilitychange', onVisibilityChange);

    return () => {
      window.clearInterval(intervalId);
      window.removeEventListener('focus', onWindowFocus);
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, [fetchActiveChats, profile?.username]);

  useEffect(() => {
    if (!chatOpen || !selectedChatId || !profile?.username) return undefined;

    let cancelled = false;
    let syncing = false;

    const syncOpenChat = async () => {
      if (syncing || cancelled) return;
      syncing = true;

      try {
        const { data, error } = await supabase
          .from('messages')
          .select(
            'id, chat_request_id, sender_username, receiver_username, message_text, message_type, media_url, duration_seconds, sent_at, is_read, status'
          )
          .eq('chat_request_id', selectedChatId)
          .order('sent_at', { ascending: true });

        if (!cancelled && !error) {
          const nextMessages = data || [];
          setChatMessages((prev) => (hasSameMessageSnapshot(prev, nextMessages) ? prev : nextMessages));
          void markChatAsRead(selectedChatId, profile.username);
        }
      } finally {
        syncing = false;
      }
    };

    void syncOpenChat();
    const intervalId = window.setInterval(() => {
      void syncOpenChat();
    }, 1000);

    return () => {
      cancelled = true;
      window.clearInterval(intervalId);
    };
  }, [chatOpen, markChatAsRead, profile?.username, selectedChatId]);

  useEffect(() => {
    if (!chatOpen || !selectedChatId || !profile?.username) return undefined;

    const channel = supabase
      .channel(`chat-room-${selectedChatId}-${profile.username}`)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'messages',
          filter: `chat_request_id=eq.${selectedChatId}`,
        },
        async (payload) => {
          const incoming = payload.new;
          setChatMessages((prev) => {
            if (prev.some((message) => isSameChatId(message.id, incoming.id))) {
              return prev;
            }
            return [...prev, incoming].sort(
              (left, right) => new Date(left.sent_at || 0).getTime() - new Date(right.sent_at || 0).getTime()
            );
          });

          if (incoming?.receiver_username === profile.username) {
            await markChatAsRead(selectedChatId, profile.username);
            setActiveChats((prev) =>
              sortChatsByPriority(
                prev.map((chat) => (isSameChatId(chat.id, selectedChatId) ? { ...chat, unreadCount: 0 } : chat))
              )
            );
            setSelectedChat((prevSelected) =>
              isSameChatId(prevSelected?.id, selectedChatId) ? { ...prevSelected, unreadCount: 0 } : prevSelected
            );
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [chatOpen, markChatAsRead, profile?.username, selectedChatId, sortChatsByPriority]);

  useEffect(() => {
    if (!profile?.username) return undefined;

    const channel = supabase
      .channel(`chat-inbox-${profile.username}`)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'messages',
          filter: `receiver_username=eq.${profile.username}`,
        },
        async (payload) => {
          const incoming = payload.new;
          const incomingChatId = incoming?.chat_request_id;
          if (!incomingChatId) return;

          const isViewingIncomingChat = chatOpen && isSameChatId(selectedChatId, incomingChatId);
          const incomingChatKey = String(incomingChatId);

          const previousUnreadCount = Number(knownUnreadCountsRef.current.get(incomingChatKey) || 0);
          knownUnreadCountsRef.current.set(
            incomingChatKey,
            isViewingIncomingChat ? 0 : previousUnreadCount + 1
          );
          void showBrowserNotification({
            title: `New message from ${incoming.sender_username || 'Unlock user'}`,
            body: incoming.message_type === 'voice' ? 'Sent you a voice note.' : 'Sent you a new message.',
            tag: `message-${incoming.id || incomingChatId}`,
            url: `/app?chat=${encodeURIComponent(incomingChatId)}`,
          });

          if (isViewingIncomingChat) {
            setChatMessages((prev) => {
              if (prev.some((message) => isSameChatId(message.id, incoming.id))) {
                return prev;
              }
              return [...prev, incoming].sort(
                (left, right) => new Date(left.sent_at || 0).getTime() - new Date(right.sent_at || 0).getTime()
              );
            });
            await markChatAsRead(incomingChatId, profile.username);
            await loadChatMessages({ id: incomingChatId }, { silent: true });
            setActiveChats((prev) =>
              sortChatsByPriority(
                prev.map((chat) => (isSameChatId(chat.id, incomingChatId) ? { ...chat, unreadCount: 0 } : chat))
              )
            );
            return;
          }

          setActiveChats((prev) => {
            if (!prev.some((chat) => isSameChatId(chat.id, incomingChatId))) return prev;
            return sortChatsByPriority(
              prev.map((chat) =>
                isSameChatId(chat.id, incomingChatId)
                  ? { ...chat, unreadCount: (chat.unreadCount || 0) + 1 }
                  : chat
              )
            );
          });
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [chatOpen, loadChatMessages, markChatAsRead, profile?.username, selectedChatId, sortChatsByPriority]);

  useEffect(() => {
    if (!profile?.username) return undefined;

    const channel = supabase
      .channel(`chat-unlocks-${profile.username}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'chat_requests',
          filter: `celebrity_username=eq.${profile.username}`,
        },
        (payload) => {
          const unlockedChat = payload.new;
          if (!unlockedChat?.id || unlockedChat.payment_confirmed !== true) return;

          const unlockId = String(unlockedChat.id);
          if (notifiedUnlockChatIdsRef.current.has(unlockId)) return;
          notifiedUnlockChatIdsRef.current.add(unlockId);

          void showBrowserNotification({
            title: 'New chat unlocked',
            body: `${unlockedChat.fan_username || 'A fan'} unlocked a 24-hour chat with you.`,
            tag: `unlock-chat-${unlockId}`,
            url: `/app?chat=${encodeURIComponent(unlockId)}`,
          });
          void fetchActiveChats({ username: profile.username }, { silent: true });
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [fetchActiveChats, profile?.username]);

  useEffect(() => {
    shouldStickToBottomRef.current = true;
  }, [chatOpen, selectedChatId]);

  useEffect(() => {
    if (!chatOpen) return;
    if (!shouldStickToBottomRef.current) return;
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'auto', block: 'end' });
    }
  }, [chatMessages, chatOpen]);

  useEffect(() => {
    if (!showEmojiPicker) return undefined;

    const handleOutsideClick = (event) => {
      if (emojiPickerRef.current && !emojiPickerRef.current.contains(event.target)) {
        setShowEmojiPicker(false);
      }
    };

    document.addEventListener('mousedown', handleOutsideClick);
    return () => {
      document.removeEventListener('mousedown', handleOutsideClick);
    };
  }, [showEmojiPicker]);

  useEffect(() => {
    return () => {
      clearVoiceRecordingInterval();
      const recorder = recordingMediaRecorderRef.current;
      if (recorder && recorder.state !== 'inactive') {
        recorder.ondataavailable = null;
        recorder.onstop = null;
        recorder.onerror = null;
        try {
          recorder.stop();
        } catch (_stopError) {
          // no-op
        }
      }
      recordingMediaRecorderRef.current = null;
      recordingChunksRef.current = [];
      recordingStartedAtRef.current = 0;
      releaseVoiceRecordingStream();
    };
  }, [clearVoiceRecordingInterval, releaseVoiceRecordingStream]);

  useEffect(() => {
    return () => {
      if (avatarPreview?.startsWith('blob:')) {
        URL.revokeObjectURL(avatarPreview);
      }
    };
  }, [avatarPreview]);

  useEffect(() => {
    return () => {
      if (paymentProofPreview?.startsWith('blob:')) {
        URL.revokeObjectURL(paymentProofPreview);
      }
    };
  }, [paymentProofPreview]);

  useEffect(() => {
    if (!isCheckingSession) {
      setShowSessionStuckHint(false);
      return undefined;
    }

    const hintTimeoutId = window.setTimeout(() => {
      setShowSessionStuckHint(true);
    }, SESSION_STUCK_HINT_DELAY_MS);

    return () => {
      window.clearTimeout(hintTimeoutId);
    };
  }, [isCheckingSession]);

  if (isCheckingSession) {
    return (
      <div className="app-shell">
        <div className="container app-loading-state">
          <p>Checking your session...</p>
          {showSessionStuckHint ? (
            <p className="dashboard-muted">
              This is taking too long. Please clear this site&apos;s cache and cookies, then reopen the site.
            </p>
          ) : null}
        </div>
      </div>
    );
  }

  if (pendingAuthUser) {
    return (
      <div className="app-shell">
        <div className="container username-setup-state">
          <div className="username-setup-card">
            <h2>Pick Your Username</h2>
            <p>Google sign-in worked. Choose your public username to continue.</p>
            {pendingAuthUser.email ? (
              <p className="username-setup-email">Signed in as: {pendingAuthUser.email}</p>
            ) : null}

            <form className="username-setup-form" onSubmit={handleUsernameSetupSubmit}>
              <label htmlFor="username-setup-input" className="settings-input-label">
                Username
              </label>
              <input
                id="username-setup-input"
                className="settings-text-input"
                type="text"
                value={usernameDraft}
                onChange={(event) => {
                  setUsernameDraft(event.target.value.toLowerCase());
                  setUsernameSetupError('');
                }}
                placeholder="choose_username"
                autoComplete="username"
                disabled={savingUsernameSetup}
              />
              <p className="username-setup-hint">
                Use 2-20 lowercase letters, numbers, or underscores.
              </p>

              {usernameSetupError ? <p className="username-setup-error">{usernameSetupError}</p> : null}

              <div className="settings-actions username-setup-actions">
                <button type="submit" className="settings-primary-btn" disabled={savingUsernameSetup}>
                  {savingUsernameSetup ? 'Saving...' : 'Save Username'}
                </button>
                <button
                  type="button"
                  className="settings-secondary-btn"
                  onClick={handleLogout}
                  disabled={savingUsernameSetup}
                >
                  Sign Out
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    );
  }

  if (!profile) {
    return null;
  }

  return (
    <div className="app-shell">
      <header className="app-topbar">
        <div className="container app-topbar-content">
          <div className="app-brand">
            <div className="logo-img-wrapper">
              <img src={lockImage} alt="Unlock Logo" className="logo-img" />
            </div>
            <div className="logo-text">Unlock</div>
          </div>

          <div className="app-user-chip">
            {profile.avatar_url ? (
              <img src={profile.avatar_url} alt={`${displayName} avatar`} className="app-user-chip-avatar" />
            ) : (
              <div className="app-user-chip-avatar-fallback">{getAvatarInitial(displayName)}</div>
            )}
            <div className="app-user-chip-text">
              <strong>{displayName}</strong>
              <span>{profile.email || 'No email available'}</span>
            </div>
            <div className="app-user-chip-metric">
              <span>Active</span>
              <strong>{activeChats.length}</strong>
            </div>
          </div>

          <div className="app-header-actions">
            <button
              type="button"
              className="app-share-link-btn app-share-link-btn-desktop"
              onClick={handleCopyProfileLink}
              disabled={!profileShareUrl}
            >
              {copyProfileLinkState || 'Copy Profile Link'}
            </button>
            <button
              type="button"
              className="app-settings-btn"
              onClick={openSettingsModal}
              aria-label="Open settings"
              title="Settings"
            >
              <span aria-hidden="true">{'\u2699'}</span>
            </button>
            <button type="button" className="auth-logout-btn" onClick={handleLogout}>
              Logout
            </button>
          </div>

          <button
            type="button"
            className="app-share-link-btn app-share-link-btn-mobile"
            onClick={handleCopyProfileLink}
            disabled={!profileShareUrl}
          >
            {copyProfileLinkState || 'Copy Profile Link'}
          </button>
        </div>
      </header>

      <main className="app-main">
        <div className="container app-main-stack">
          <section className="app-card app-chats-card">
            <div className="app-search-panel">
              <h3 className="app-search-title">Search Users</h3>
              <form className="app-search-form" onSubmit={handleUserSearch}>
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(event) => setSearchQuery(event.target.value)}
                  className="app-search-input"
                  placeholder="Search by username"
                  aria-label="Search users by username"
                />
                <button type="submit" className="app-search-btn" disabled={searchingUsers}>
                  {searchingUsers ? 'Searching...' : 'Search'}
                </button>
              </form>

              {searchError ? <p className="dashboard-error">{searchError}</p> : null}
              {!searchError && hasSearchedUsers && !searchingUsers && searchResults.length === 0 ? (
                <p className="dashboard-muted">No users found.</p>
              ) : null}

              {searchResults.length > 0 ? (
                <div className="search-results-list">
                  {searchResults.map((user) => {
                    const localRestriction = getUnlockRestrictionReason(user);
                    const isUnlockingThisUser = unlockingUsername === user.username;
                    const unlockDisabled = Boolean(localRestriction) || isUnlockingThisUser || submittingPaymentProof;
                    const buttonTitle = localRestriction || `Unlock ${user.username}`;
                    const ratingCount = Number(user.rating_count || 0);
                    const normalizedRating = Number(
                      user.user_rating || (ratingCount > 0 ? Number(user.total_rating || 0) / ratingCount : 0)
                    );
                    const ratingLabel =
                      ratingCount > 0 && Number.isFinite(normalizedRating)
                        ? `${normalizedRating.toFixed(1)}★ (${ratingCount} ratings)`
                        : 'No ratings yet';

                    return (
                      <article key={user.id || user.username} className="search-user-item">
                        <div className="search-user-main">
                          {user.avatar_url ? (
                            <img src={user.avatar_url} alt={`${user.username} avatar`} className="search-user-avatar" />
                          ) : (
                            <div className="search-user-avatar-fallback">
                              {getAvatarInitial(user.username)}
                            </div>
                          )}
                          <div className="search-user-text">
                            <strong>{user.username}</strong>
                            <span>Rate: {user.user_rate || 100} EGP</span>
                            <span className="search-user-rating">Rating: {ratingLabel}</span>
                          </div>
                        </div>
                        <button
                          type="button"
                          className="search-lock-btn"
                          title={buttonTitle}
                          aria-label={`Unlock ${user.username}`}
                          onClick={() => handleUnlockClick(user)}
                          disabled={unlockDisabled}
                        >
                          {isUnlockingThisUser ? (
                            <span className="search-lock-loading">...</span>
                          ) : (
                            <img src={lockImage} alt="" />
                          )}
                        </button>
                      </article>
                    );
                  })}
                </div>
              ) : null}
            </div>

            <div className="app-card-header">
              <div className="app-card-heading">
                <h2 className="app-card-title">
                  Active Chats <span className="app-card-title-count">({activeChats.length})</span>
                </h2>
                <p className="app-card-subtitle">Your currently unlocked conversations.</p>
              </div>
              <button
                type="button"
                className="refresh-chats-btn"
                onClick={refreshActiveChats}
                disabled={loadingChats}
              >
                {loadingChats ? 'Refreshing...' : 'Refresh'}
              </button>
            </div>

            {chatsError ? <p className="dashboard-error">{chatsError}</p> : null}
            {!chatsError && loadingChats && activeChats.length === 0 ? (
              <p className="dashboard-muted">Loading active chats...</p>
            ) : null}
            {!chatsError && !loadingChats && activeChats.length === 0 ? (
              <p className="dashboard-muted">No active chats right now.</p>
            ) : null}
            {activeChats.length > 0 ? (
              <div className="active-chat-list">
                {activeChats.map((chat) => (
                  <article key={chat.id} className="active-chat-item">
                    <button
                      type="button"
                      className="active-chat-open-btn"
                      onClick={() => openChat(chat)}
                    >
                      <div className="active-chat-user">
                        {chat.otherUserAvatar ? (
                          <img
                            src={chat.otherUserAvatar}
                            alt={`${chat.otherUsername} avatar`}
                            className="active-chat-avatar"
                          />
                        ) : (
                          <div className="active-chat-avatar-fallback">
                            {getAvatarInitial(chat.otherUsername)}
                          </div>
                        )}
                        <div className="active-chat-text">
                          <strong className="chat-participant-name">
                            {chat.otherUsername || 'Unknown user'}
                            {getGenderIndicator(chat.otherUserGender) ? (
                              <span
                                className={`chat-gender-indicator ${getGenderIndicator(chat.otherUserGender).className}`}
                                title={getGenderIndicator(chat.otherUserGender).label}
                                aria-label={getGenderIndicator(chat.otherUserGender).label}
                              >
                                {getGenderIndicator(chat.otherUserGender).symbol}
                              </span>
                            ) : null}
                          </strong>
                          <span>Expires in {getTimeRemaining(chat.chat_expiry_time)}</span>
                        </div>
                      </div>
                      <div className="active-chat-meta">
                        {chat.unreadCount > 0 ? (
                          <span className="active-chat-unread-badge">
                            {chat.unreadCount > 99 ? '99+' : chat.unreadCount}
                          </span>
                        ) : null}
                        <span className="active-chat-open-label">Open Chat</span>
                      </div>
                    </button>
                  </article>
                ))}
              </div>
            ) : null}
          </section>
        </div>
      </main>

      {settingsOpen ? (
        <div className="settings-modal-overlay" onClick={closeSettingsModal}>
          <div className="settings-modal" onClick={(event) => event.stopPropagation()}>
            <div className="settings-modal-header">
              <h3>Settings</h3>
              <button type="button" className="settings-close-btn" onClick={closeSettingsModal}>
                <span aria-hidden="true">✕</span>
              </button>
            </div>

            <div className="settings-tabs">
              <button
                type="button"
                className={`settings-tab ${settingsTab === 'general' ? 'active' : ''}`}
                onClick={() => setSettingsTab('general')}
              >
                General
              </button>
              <button
                type="button"
                className={`settings-tab ${settingsTab === 'payment' ? 'active' : ''}`}
                onClick={() => setSettingsTab('payment')}
              >
                Payment
              </button>
              <button
                type="button"
                className={`settings-tab ${settingsTab === 'support' ? 'active' : ''}`}
                onClick={() => setSettingsTab('support')}
              >
                Support
              </button>
            </div>

            {settingsAlert.message ? (
              <div
                className={`settings-alert ${
                  settingsAlert.type === 'success' ? 'settings-alert-success' : 'settings-alert-error'
                }`}
              >
                {settingsAlert.message}
              </div>
            ) : null}

            <div className="settings-modal-body">
              {settingsTab === 'general' ? (
                <>
                  <section className="settings-section">
                    <h4>Notifications</h4>
                    <p>Get alerted when someone sends you a message or unlocks a new chat.</p>
                    <div className="settings-notification-row">
                      <span className={`settings-notification-status settings-notification-status-${notificationPermission}`}>
                        {notificationPermission === 'granted'
                          ? 'Notifications enabled'
                          : notificationPermission === 'denied'
                            ? 'Notifications blocked'
                            : notificationPermission === 'unsupported'
                              ? 'Not supported in this browser'
                              : 'Notifications not enabled'}
                      </span>
                      {notificationPermission !== 'granted' ? (
                        <button
                          type="button"
                          className="settings-primary-btn"
                          onClick={handleEnableNotifications}
                          disabled={requestingNotificationPermission || notificationPermission === 'unsupported'}
                        >
                          {requestingNotificationPermission ? 'Enabling...' : 'Allow Notifications'}
                        </button>
                      ) : null}
                    </div>
                  </section>

                  <section className="settings-section">
                    <h4>Profile Picture</h4>
                    <p>Add or change your profile picture.</p>
                    <input
                      ref={avatarInputRef}
                      type="file"
                      accept="image/*"
                      className="settings-hidden-file-input"
                      onChange={handleAvatarChange}
                    />
                    <button
                      type="button"
                      className="settings-avatar-picker"
                      onClick={() => avatarInputRef.current?.click()}
                      disabled={savingGeneral}
                    >
                      {avatarPreview ? (
                        <img src={avatarPreview} alt="Profile avatar preview" className="settings-avatar-preview" />
                      ) : (
                        <div className="settings-avatar-placeholder">{getAvatarInitial(displayName)}</div>
                      )}
                      <span>{savingGeneral ? 'Saving...' : 'Tap to change'}</span>
                    </button>
                  </section>

                  <section className="settings-section">
                    <h4>Who can chat with me?</h4>
                    <div className="settings-choice-list">
                      {CHAT_PREFERENCE_OPTIONS.map((option) => (
                        <button
                          key={option.value}
                          type="button"
                          className={`settings-choice ${
                            userChatPreference === option.value ? 'settings-choice-active' : ''
                          }`}
                          onClick={() => setUserChatPreference(option.value)}
                          disabled={savingGeneral}
                        >
                          {option.label}
                        </button>
                      ))}
                    </div>
                  </section>

                  <section className="settings-section">
                    <h4>Set Your Rate</h4>
                    <p>Choose one of the available rates.</p>
                    <div className="settings-rate-grid">
                      {RATE_OPTIONS.map((rate) => (
                        <button
                          key={rate}
                          type="button"
                          className={`settings-rate-pill ${userRate === rate ? 'settings-rate-pill-active' : ''}`}
                          onClick={() => setUserRate(rate)}
                          disabled={savingGeneral}
                        >
                          {rate} EGP
                        </button>
                      ))}
                    </div>
                    <div className="settings-earnings-box">
                      <p>User pays: {earnings.rate} EGP</p>
                      <p>App Fee (13%): {earnings.appFee} EGP</p>
                      <p className="settings-earnings-highlight">You get: {earnings.userEarnings} EGP</p>
                    </div>
                  </section>

                  <div className="settings-actions">
                    <button
                      type="button"
                      className="settings-primary-btn"
                      onClick={handleSaveGeneralSettings}
                      disabled={savingGeneral}
                    >
                      {savingGeneral ? 'Saving...' : 'Save'}
                    </button>
                    <button
                      type="button"
                      className="settings-secondary-btn"
                      onClick={closeSettingsModal}
                      disabled={savingGeneral}
                    >
                      Cancel
                    </button>
                  </div>
                </>
              ) : null}

              {settingsTab === 'payment' ? (
                <>
                  <section className="settings-section">
                    <h4>Payment Settings</h4>
                    <p>Add your Instapay number to receive payments when users unlock chats.</p>
                  </section>

                  <section className="settings-section">
                    {profile.user_instapay ? (
                      <div className="settings-current-instapay">
                        Current Instapay: {'\u2022\u2022\u2022\u2022\u2022\u2022'}
                        {profile.user_instapay.slice(-4)}
                      </div>
                    ) : (
                      <div className="settings-no-instapay">
                        You have not set an Instapay number yet.
                      </div>
                    )}
                    <label className="settings-input-label" htmlFor="settings-instapay-input">
                      Instapay Number
                    </label>
                    <input
                      id="settings-instapay-input"
                      className="settings-text-input"
                      type="tel"
                      value={userInstapay}
                      onChange={(event) => setUserInstapay(event.target.value)}
                      placeholder="01XXXXXXXXX"
                      maxLength={11}
                      disabled={savingPayment}
                    />
                    <p className="settings-input-hint">
                      Enter your 11-digit Egyptian phone number (for example: 01012345678).
                    </p>
                  </section>

                  <section className="settings-section">
                    <h4>Important Information</h4>
                    <ul className="settings-notes-list">
                      <li>This number is used to send your earnings when someone unlocks a chat.</li>
                      <li>Your number is saved at chat start time for that specific chat.</li>
                      <li>Updating your number later only affects new chats.</li>
                      <li>Fans cannot see your Instapay number.</li>
                      <li>
                        You receive {earnings.userEarnings} EGP for each {earnings.rate} EGP chat (13% platform fee).
                      </li>
                    </ul>
                  </section>

                  <div className="settings-actions">
                    <button
                      type="button"
                      className="settings-primary-btn"
                      onClick={handleSaveInstapay}
                      disabled={savingPayment}
                    >
                      {savingPayment ? 'Saving...' : 'Save Instapay'}
                    </button>
                    <button
                      type="button"
                      className="settings-secondary-btn"
                      onClick={closeSettingsModal}
                      disabled={savingPayment}
                    >
                      Cancel
                    </button>
                  </div>
                </>
              ) : null}

              {settingsTab === 'support' ? (
                <div className="settings-support-wrap">
                  <div className="settings-support-icon">🆘</div>
                  <h4>Need Help?</h4>
                  <p>
                    If you have any issue or need assistance, contact our support team and include your username for a
                    faster response.
                  </p>
                  <a href="mailto:support@unlockapp.me" className="settings-support-email">
                    support@unlockapp.me
                  </a>
                  <ul className="settings-notes-list">
                    <li>Describe the issue with as much detail as possible.</li>
                    <li>Include screenshots when relevant.</li>
                    <li>Typical response time is 24-48 hours.</li>
                  </ul>
                </div>
              ) : null}
            </div>
          </div>
        </div>
      ) : null}

      {ratingModalOpen && currentRatingChat ? (
        <div className="rating-modal-overlay">
          <div className="rating-modal" onClick={(event) => event.stopPropagation()}>
            <h3 className="rating-modal-title">Rate Your Chat</h3>
            <p className="rating-modal-subtitle">
              How was your chat with <strong>{currentRatingChat.celebrity_username}</strong>?
            </p>

            <div className="rating-modal-avatar-wrap">
              {currentRatingChat.celebrityAvatar ? (
                <img
                  src={currentRatingChat.celebrityAvatar}
                  alt={`${currentRatingChat.celebrity_username} avatar`}
                  className="rating-modal-avatar"
                />
              ) : (
                <div className="rating-modal-avatar-fallback">
                  {getAvatarInitial(currentRatingChat.celebrity_username)}
                </div>
              )}
              <p className="rating-modal-username">{currentRatingChat.celebrity_username}</p>
            </div>

            <div className="rating-zero-wrap">
              <button
                type="button"
                className={`rating-zero-btn ${selectedRating === 0 ? 'rating-zero-btn-active' : ''}`}
                onClick={() => setSelectedRating(0)}
                disabled={submittingRating}
              >
                0{'\u2605'} (No response)
              </button>
            </div>

            <div className="rating-stars-row">
              {CHAT_RATING_OPTIONS.map((star) => (
                <button
                  key={star}
                  type="button"
                  className={`rating-star-btn ${selectedRating === star ? 'rating-star-btn-active' : ''}`}
                  onClick={() => setSelectedRating(star)}
                  disabled={submittingRating}
                  aria-label={`Rate ${star} star${star > 1 ? 's' : ''}`}
                >
                  <span className="rating-star-icon">
                    {selectedRating !== null && selectedRating >= star ? '\u2605' : '\u2606'}
                  </span>
                </button>
              ))}
            </div>

            <p className="rating-selection-label">{getRatingSelectionLabel(selectedRating)}</p>
            {ratingError ? <p className="rating-modal-error">{ratingError}</p> : null}

            <div className="rating-modal-actions">
              <button
                type="button"
                className="settings-primary-btn"
                onClick={handleSubmitRating}
                disabled={submittingRating}
              >
                {submittingRating ? 'Submitting...' : 'Submit Rating'}
              </button>
              <button
                type="button"
                className="settings-secondary-btn"
                onClick={handleSkipRating}
                disabled={submittingRating}
              >
                Skip
              </button>
            </div>
            {unratedChats.length > 1 ? (
              <p className="rating-modal-queue-note">{unratedChats.length - 1} more pending rating(s).</p>
            ) : null}
          </div>
        </div>
      ) : null}

      {unlockModalOpen && currentUnlockTarget ? (
        <div
          className="payment-proof-overlay"
          onClick={() => {
            if (!submittingPaymentProof) {
              closeUnlockModal();
            }
          }}
        >
          <div className="payment-proof-modal" onClick={(event) => event.stopPropagation()}>
            <div className="payment-proof-header">
              <h3>Upload Payment Proof</h3>
              <button
                type="button"
                className="payment-proof-close-btn"
                onClick={closeUnlockModal}
                disabled={submittingPaymentProof}
              >
                <span aria-hidden="true">x</span>
              </button>
            </div>

            <p className="payment-proof-subtitle">
              Send <strong>{currentUnlockTarget.user_rate || 100} EGP</strong> to{' '}
              <strong>
                {currentUnlockTarget.username}'s Instapay ({targetInstapay || 'not available'})
              </strong>
              . {currentUnlockTarget.username} is not obligated to reply to your messages. Then upload a clear
              screenshot.
            </p>

            {unlockError ? <p className="payment-proof-error">{unlockError}</p> : null}

            <input
              ref={paymentProofInputRef}
              type="file"
              accept="image/*"
              className="settings-hidden-file-input"
              onChange={handlePaymentProofSelection}
            />

            <button
              type="button"
              className="payment-proof-picker-btn"
              onClick={() => paymentProofInputRef.current?.click()}
              disabled={submittingPaymentProof}
            >
              {paymentProofPreview ? 'Change Image' : 'Select Image'}
            </button>

            {paymentProofPreview ? (
              <div className="payment-proof-preview-wrap">
                <img src={paymentProofPreview} alt="Payment proof preview" className="payment-proof-preview" />
              </div>
            ) : null}

            <div className="payment-proof-actions">
              <button
                type="button"
                className="settings-primary-btn"
                onClick={submitPaymentProof}
                disabled={submittingPaymentProof || !paymentProofFile}
              >
                {submittingPaymentProof ? 'Submitting...' : 'Submit Payment Proof'}
              </button>
              <button
                type="button"
                className="settings-secondary-btn"
                onClick={closeUnlockModal}
                disabled={submittingPaymentProof}
              >
                Cancel
              </button>
            </div>

            <ul className="payment-proof-notes">
              <li>Upload a full screenshot with amount, date, time, and transaction ID visible.</li>
              <li>Verification can take up to 24 hours.</li>
              <li>Chat activates after payment verification.</li>
            </ul>
          </div>
        </div>
      ) : null}

      {chatOpen && selectedChat ? (
        <div className="chat-modal-overlay" onClick={closeChat}>
          <div className="chat-modal" onClick={(event) => event.stopPropagation()}>
            <div className="chat-modal-header">
              <div className="chat-modal-user">
                {selectedChat.otherUserAvatar ? (
                  <img
                    src={selectedChat.otherUserAvatar}
                    alt={`${selectedChat.otherUsername} avatar`}
                    className="chat-modal-avatar"
                  />
                ) : (
                  <div className="chat-modal-avatar-fallback">
                    {getAvatarInitial(selectedChat.otherUsername)}
                  </div>
                )}
                <div className="chat-modal-user-text">
                  <strong className="chat-participant-name">
                    {selectedChat.otherUsername || 'Unknown user'}
                    {getGenderIndicator(selectedChat.otherUserGender) ? (
                      <span
                        className={`chat-gender-indicator ${getGenderIndicator(selectedChat.otherUserGender).className}`}
                        title={getGenderIndicator(selectedChat.otherUserGender).label}
                        aria-label={getGenderIndicator(selectedChat.otherUserGender).label}
                      >
                        {getGenderIndicator(selectedChat.otherUserGender).symbol}
                      </span>
                    ) : null}
                  </strong>
                  <span>Expires in {getTimeRemaining(selectedChat.chat_expiry_time)}</span>
                </div>
              </div>
              <button type="button" className="chat-modal-close-btn" onClick={closeChat}>
                Close
              </button>
            </div>

            <div className="chat-modal-body" ref={chatBodyRef} onScroll={handleChatBodyScroll}>
              {messagesError ? <p className="dashboard-error">{messagesError}</p> : null}
              {!messagesError && loadingMessages ? (
                <p className="dashboard-muted">Loading messages...</p>
              ) : null}
              {!messagesError && !loadingMessages && chatMessages.length === 0 ? (
                <p className="dashboard-muted">No messages yet. Say hi.</p>
              ) : null}

              {chatMessages.length > 0 ? (
                <div className="chat-message-list">
                  {chatMessages.map((message) => {
                    const isOwnMessage = message.sender_username === profile.username;
                    const deliveryState = getMessageDeliveryState(message);
                    const isVoiceMessage =
                      Boolean(message.media_url) &&
                      (message.message_type === 'voice' || Number(message.duration_seconds || 0) > 0);
                    return (
                      <div
                        key={message.id}
                        className={`chat-message-row ${isOwnMessage ? 'chat-message-row-own' : ''}`}
                      >
                        <div
                          className={`chat-bubble ${isOwnMessage ? 'chat-bubble-own' : ''} ${
                            isVoiceMessage ? 'chat-bubble-voice' : ''
                          }`}
                        >
                          {isVoiceMessage ? (
                            <div className="chat-voice-note">
                              <audio controls preload="metadata" className="chat-voice-audio" src={message.media_url}>
                                Your browser does not support audio playback.
                              </audio>
                              <span className="chat-voice-duration">
                                {formatDurationLabel(message.duration_seconds)}
                              </span>
                            </div>
                          ) : (
                            <p>{message.message_text}</p>
                          )}
                          <div className="chat-message-meta">
                            <span className="chat-message-time">{formatMessageTime(message.sent_at)}</span>
                            {isOwnMessage ? (
                              <span
                                className={`chat-message-status ${
                                  deliveryState === 'seen' ? 'chat-message-status-seen' : 'chat-message-status-sent'
                                }`}
                                aria-label={deliveryState === 'seen' ? 'Seen' : 'Sent'}
                                title={deliveryState === 'seen' ? 'Seen' : 'Sent'}
                              >
                                {deliveryState === 'seen' ? '✓✓' : '✓'}
                              </span>
                            ) : null}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                  <div ref={messagesEndRef} />
                </div>
              ) : null}
            </div>

            <form className="chat-input-form" onSubmit={handleSendMessage}>
              <div className="chat-input-main">
                <div className="chat-input-wrap" ref={emojiPickerRef}>
                  <input
                    type="text"
                    value={newMessage}
                    onChange={(event) => setNewMessage(event.target.value)}
                    className="chat-input"
                    placeholder="Type a message..."
                    disabled={sendingMessage}
                  />
                  <button
                    type="button"
                    className="emoji-toggle-btn"
                    onClick={() => setShowEmojiPicker((prev) => !prev)}
                    aria-label="Open emoji picker"
                    title="Add emoji"
                  >
                    😊
                  </button>

                  {showEmojiPicker ? (
                    <div className="emoji-picker">
                      {CHAT_EMOJIS.map((emoji) => (
                        <button
                          key={emoji}
                          type="button"
                          className="emoji-btn"
                          onClick={() => insertEmoji(emoji)}
                          aria-label={`Insert ${emoji}`}
                        >
                          {emoji}
                        </button>
                      ))}
                    </div>
                  ) : null}
                </div>
                <button
                  type="button"
                  className={`chat-voice-btn ${isRecordingVoice ? 'chat-voice-btn-recording' : ''}`}
                  onClick={isRecordingVoice ? stopVoiceRecording : startVoiceRecording}
                  disabled={sendingMessage || !supportsVoiceNotes}
                  aria-label={isRecordingVoice ? 'Stop voice recording' : 'Start voice recording'}
                  title={
                    supportsVoiceNotes
                      ? isRecordingVoice
                        ? 'Stop recording'
                        : 'Record voice note'
                      : 'Voice notes are not supported in this browser'
                  }
                >
                  {isRecordingVoice ? (
                    <span className="chat-voice-stop-dot" aria-hidden="true" />
                  ) : (
                    <span className="chat-voice-icon" aria-hidden="true">
                      <svg viewBox="0 0 24 24" focusable="false">
                        <path d="M12 15a4 4 0 0 0 4-4V6a4 4 0 0 0-8 0v5a4 4 0 0 0 4 4Zm-6-4a1 1 0 1 1 2 0a4 4 0 0 0 8 0a1 1 0 1 1 2 0a6 6 0 0 1-5 5.91V20h2a1 1 0 1 1 0 2H9a1 1 0 1 1 0-2h2v-3.09A6 6 0 0 1 6 11Z" />
                      </svg>
                    </span>
                  )}
                </button>
                <button
                  type="submit"
                  className="chat-send-btn"
                  disabled={sendingMessage || isRecordingVoice || !newMessage.trim()}
                >
                  {sendingMessage ? 'Sending...' : 'Send'}
                </button>
              </div>
              {isRecordingVoice ? (
                <p className="chat-recording-indicator">Recording voice note... {formatDurationLabel(recordingSeconds)}</p>
              ) : null}
              {uploadingVoice ? <p className="chat-recording-indicator">Uploading voice note...</p> : null}
              {voiceError ? <p className="chat-voice-error">{voiceError}</p> : null}
            </form>
          </div>
        </div>
      ) : null}
    </div>
  );
}

export default UnlockAppPage;

